import { useEffect, useRef, useState } from 'react';
import cdoPlace from '../cdo/cdoPlace.js';
import cdoGeneratedWorld from '../cdo/cdoGeneratedWorld.js';
import PlaceTreeStoryPanel from './PlaceTreeStoryPanel.js';
import PlaceTreeDevelopment from './PlaceTreeDevelopmentPanel.js';
import { createPlaceTreeAutomatically, getAutomationCatalog } from './placeTreeAutomation.js';
import { planWorldSocialLives } from './placeTreeSocialPlan.js';
import { createPlaceTreeAutomation, validateAutomationCounts, retryPlaceTreeRequest } from './placeTreeAutomationSettings.js';
import { createEmptyWorldDevelopment, getRecordPreparationTasks } from './placeTreeRecordPreparation.js';
import { createWorldDraftId, createWorldSaveProgress, getWorldDraftCatalog, getExistingWorldRelationships, isWorldSaveComplete, savePlaceTreeWorld } from './placeTreeWorldSave.js';
import {
  POPULATION_TRANCHES,
  canGeneratePopulationTranche,
  ensureDraftBeingIds,
  ensureDraftPlaceIds,
  generatePopulationBatchesSerially,
  getHouseholdManifestProgress,
  getNextPopulationBatchRequest,
  getPopulationTrancheCounts,
  getResidentTranchePlanTotal,
  isResidentTranchePlanValid,
  isHouseholdManifestTranche,
  invalidateHouseholdManifestsFrom,
  mergeInhabitantSuggestionBatches,
  normalizePopulationTranche,
  normalizeHouseholdManifest,
  normalizeResidentTranchePlan,
  orderChildPlaceSuggestionEntriesByHierarchy,
  selectCurrentResidentDraftsForRequest,
} from './generateTownDraftGraph.js';
import {
  COMMERCIAL_INVENTORY_BATCH_SIZE,
  generateCommercialInventoryBatchesSerially,
  getInventoryDraftPlaces,
  getCommercialInventoryProgress,
  getNextCommercialInventoryBatch,
  validateCommercialInventoryManifest,
} from './commercialInventoryDraft.js';
import {
  NOTABLE_LOADOUT_BATCH_SIZE,
  attachNotableCurrencyHoldings,
  generateNotableLoadoutBatchesSerially,
  getNextNotableLoadoutBatch,
  getNotableLoadoutProgress,
  normalizeNotableLoadoutManifest,
  validateNotableLoadoutManifest,
} from './notableLoadoutDraft.js';
import {
  clearPlaceTreeSessionDraft,
  createEmptyPlaceTreeSessionDraft,
  hasPlaceTreeSessionContent,
  hasLargePlaceTreeSessionDraft,
  loadLargePlaceTreeSessionDraft,
  loadPlaceTreeSessionDraft,
  savePlaceTreeSessionDraft,
  savePlaceTreeSaveProgress,
  listPlaceTreeBackups,
  purgePlaceTreeBackups,
  recoverPlaceTreeBackup,
} from './placeTreeSessionDraft.js';

const DEFAULT_AOG_YEAR = 21000;
const CHILD_SUGGESTION_BATCH_SIZE = 12;
const INHABITANT_SUGGESTION_BATCH_SIZE = 8;

const createEmptyGraph = () => ({
  rootPlaceId: '',
  placesById: {},
  beingsById: {},
  itemsById: {},
});

const createRunSummary = (action) => ({
  action,
  placesCreated: 0,
  failures: 0,
});

const appendUniqueId = (values, nextValue) => {
  if (!nextValue) {
    return Array.isArray(values) ? values : [];
  }

  const normalizedValues = Array.isArray(values) ? values : [];
  return normalizedValues.includes(nextValue) ? normalizedValues : [...normalizedValues, nextValue];
};

const formatErrorMessage = (err, response) => {
  const source = response?.error ?? response ?? err;

  if (typeof source === 'string') {
    return source;
  }

  if (typeof source === 'number') {
    return `Request failed with status ${source}.`;
  }

  if (!source || typeof source !== 'object') {
    return 'Request failed.';
  }

  const parts = [];
  if (source.stage) {
    parts.push(`Stage: ${source.stage}`);
  }
  if (source.code) {
    parts.push(`Code: ${source.code}`);
  }
  if (source.message) {
    parts.push(source.message);
  } else if (source.sqlMessage) {
    parts.push(source.sqlMessage);
  } else if (source.error && typeof source.error === 'string') {
    parts.push(source.error);
  }

  if (parts.length > 0) {
    return parts.join(' | ');
  }

  try {
    return JSON.stringify(source);
  } catch {
    return 'Request failed.';
  }
};

const callCdo = (fn, ...args) =>
  new Promise((resolve, reject) => {
    fn(...args, (err, response) => {
      if (err || response?.error) {
        reject(Object.assign(new Error(formatErrorMessage(err, response)), {
          status: typeof err === 'number' ? err : response?.status ?? err?.status,
          code: response?.error?.code,
        }));
        return;
      }

      resolve(response);
    });
  });

const getPlaceLabel = (place) =>
  place?.name || place?.title || place?.entityName || place?.entityTitle || place?.idEntity || 'Unnamed place';

const normalizeExpectedChildPlaceCount = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

const normalizeExpectedInhabitantCount = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

const normalizePlannedPopulation = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

const normalizeChildPlacePlan = (plan) =>
  Array.isArray(plan)
    ? plan
        .map((entry) => ({
          category: String(entry?.category ?? '').trim(),
          expectedCount: normalizeExpectedChildPlaceCount(entry?.expectedCount),
          description: String(entry?.description ?? '').trim(),
        }))
        .filter((entry) => entry.category || entry.description || entry.expectedCount > 0)
    : [];

const getChildPlacePlanTotal = (plan) =>
  normalizeChildPlacePlan(plan).reduce((total, entry) => total + normalizeExpectedChildPlaceCount(entry.expectedCount), 0);

const resolveExpectedChildPlaceCount = (place) => {
  const explicitCount = normalizeExpectedChildPlaceCount(place?.expectedChildPlaceCount);
  return explicitCount > 0 ? explicitCount : getChildPlacePlanTotal(place?.childPlacePlan);
};

const formatPlannedPopulation = (value) => normalizePlannedPopulation(value).toLocaleString();

const formatGoldValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? `${numericValue.toLocaleString(undefined, { maximumFractionDigits: 6 })} gp`
    : '0 gp';
};

const formatCurrencyBreakdown = (currencies) =>
  (Array.isArray(currencies) ? currencies : [])
    .map((currency) =>
      `${Math.max(0, Math.floor(Number(currency?.quantity) || 0)).toLocaleString()} ${currency?.name || currency?.currencyName || 'Unknown'}`
    )
    .join(', ') || 'None';

const normalizeInhabitantPlaceRelationships = (relationships) => {
  const seen = new Set();

  return (Array.isArray(relationships) ? relationships : [])
    .map((relationship) => ({
      targetPlaceId: String(relationship?.targetPlaceId ?? '').trim(),
      relateType: String(relationship?.relateType ?? '').trim(),
      reason: String(relationship?.reason ?? '').trim(),
    }))
    .filter((relationship) => {
      if (!relationship.targetPlaceId || !relationship.relateType) {
        return false;
      }

      const key = `${relationship.targetPlaceId}|${relationship.relateType}`;
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
};

const normalizeInhabitantResidentRelationships = (relationships) =>
  (Array.isArray(relationships) ? relationships : [])
    .map((relationship) => ({
      targetResidentName: String(relationship?.targetResidentName ?? '').trim(),
      ...(relationship?.score !== undefined ? { score: relationship.score } : {}),
      relateType: String(relationship?.relateType ?? '').trim(),
      reason: String(relationship?.reason ?? '').trim(),
    }))
    .filter((relationship) => relationship.targetResidentName && relationship.relateType);

const normalizeInhabitantSuggestions = (suggestions) =>
  ensureDraftBeingIds(
    (Array.isArray(suggestions) ? suggestions : [])
    .map((suggestion) => ({
      ...suggestion,
      draftBeingId: String(suggestion?.draftBeingId ?? '').trim(),
      name: suggestion?.name ?? '',
      level: suggestion?.level ?? 1,
      title: suggestion?.title ?? '',
      beingType: suggestion?.beingType ?? '',
      description: suggestion?.description ??
        [suggestion?.role, suggestion?.characterDescription].filter(Boolean).join(': '),
      populationTranche: normalizePopulationTranche(suggestion?.populationTranche),
      populationPlanSlotId: String(suggestion?.populationPlanSlotId ?? '').trim(),
      householdKey: String(suggestion?.householdKey ?? '').trim(),
      householdName: String(suggestion?.householdName ?? '').trim(),
      householdRole: String(suggestion?.householdRole ?? '').trim(),
      memberOfPlaceId: String(suggestion?.memberOfPlaceId ?? '').trim(),
      placeRelationships: normalizeInhabitantPlaceRelationships(suggestion?.placeRelationships),
      residentRelationships: normalizeInhabitantResidentRelationships(
        suggestion?.residentRelationships
      ),
      savedBeingId: suggestion?.savedBeingId ?? '',
      savedBeingName: suggestion?.savedBeingName ?? '',
    }))
    .filter((suggestion) =>
      (suggestion.name || suggestion.description) &&
      suggestion.populationTranche &&
      suggestion.memberOfPlaceId
    )
  );

const getBeingLabel = (being) =>
  being?.name || being?.title || being?.entityName || being?.entityTitle || being?.idEntity || 'Unnamed being';

const getItemLabel = (item) =>
  item?.name || item?.title || item?.entityName || item?.entityTitle || item?.idEntity || 'Unnamed item';

const formatTimeStamp = () =>
  new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const normalizeChildPlaceSuggestions = (suggestions) =>
  Array.isArray(suggestions)
    ? ensureDraftPlaceIds(
        suggestions
        .map((suggestion) => ({
          ...suggestion,
          draftPlaceId: suggestion?.draftPlaceId ?? '',
          name: suggestion?.name ?? '',
          parentSuggestionName: suggestion?.parentSuggestionName ?? null,
          placeType: suggestion?.placeType ?? '',
          zoneType: suggestion?.zoneType ?? '',
          buildingType: suggestion?.buildingType ?? '',
          transitPointType: suggestion?.transitPointType ?? '',
          adjacentEdge: suggestion?.adjacentEdge ?? '',
          description: suggestion?.description ?? '',
          savedPlaceId: suggestion?.savedPlaceId ?? '',
          savedPlaceName: suggestion?.savedPlaceName ?? '',
        }))
        .filter((suggestion) => suggestion.name || suggestion.description)
      )
    : [];

const normalizeSearchText = (value) => String(value ?? '').trim().toLowerCase();

const getSuggestionSearchText = (suggestion) =>
  [
    suggestion?.name,
    suggestion?.title,
    suggestion?.description,
    suggestion?.placeType,
    suggestion?.zoneType,
    suggestion?.buildingType,
    suggestion?.transitPointType,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const isPublicCirculationSuggestion = (suggestion) => {
  const placeType = normalizeSearchText(suggestion?.placeType);
  const identityText = [
    suggestion?.name,
    suggestion?.title,
    suggestion?.placeType,
    suggestion?.buildingType,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    placeType.includes('passage') ||
    /\b(road|street|lane|way|path|trail|route|alley|avenue|causeway|track|passage|corridor|walk|bridge)\b/.test(identityText)
  );
};

const isCentralSettlementAnchorSuggestion = (suggestion) => {
  if (isPublicCirculationSuggestion(suggestion)) {
    return false;
  }

  const placeType = normalizeSearchText(suggestion?.placeType);
  const zoneType = normalizeSearchText(suggestion?.zoneType);
  const buildingType = normalizeSearchText(suggestion?.buildingType);
  const text = getSuggestionSearchText(suggestion);
  const anchorType = /\b(keep|castle|citadel|palace|town hall|city hall|civic hall|council hall|courthouse|great hall|market square|central market)\b/;
  const centralLanguage = /\b(central|center|centre|heart|core|middle|inner court|around the keep|before the keep|seat of|fixed heart)\b/;

  return (
    anchorType.test(buildingType) ||
    anchorType.test(text) ||
    (placeType.includes('fortification') && /\b(keep|castle|citadel)\b/.test(text)) ||
    (placeType.includes('courtyard') && centralLanguage.test(text)) ||
    ((zoneType.includes('government') || zoneType.includes('civic')) && centralLanguage.test(text))
  );
};

const getChildPlacementPriority = (suggestion) => {
  if (isPublicCirculationSuggestion(suggestion)) {
    return 0;
  }

  if (isCentralSettlementAnchorSuggestion(suggestion)) {
    return 1;
  }

  const zoneType = normalizeSearchText(suggestion?.zoneType);
  const text = getSuggestionSearchText(suggestion);

  if (zoneType.includes('military')) {
    return 2;
  }

  if (zoneType.includes('commercial')) {
    return 3;
  }

  if (zoneType.includes('residential')) {
    return 4;
  }

  if (zoneType.includes('industrial')) {
    return 5;
  }

  if (zoneType.includes('agricultural')) {
    return 6;
  }

  if (/\b(barracks|guard|gatehouse|watch|armory|arsenal|militia)\b/.test(text)) {
    return 2;
  }

  if (/\b(market|shop|inn|tavern|alehouse|stable|granary|caravan|trade)\b/.test(text)) {
    return 3;
  }

  if (/\b(home|house|row|loft|tenement|boarding|cottage|dwelling)\b/.test(text)) {
    return 4;
  }

  if (/\b(forge|smith|mill|bakery|workshop|kiln|yard|works)\b/.test(text)) {
    return 5;
  }

  if (/\b(farm|field|barn|orchard|pasture|paddock)\b/.test(text)) {
    return 6;
  }

  return 7;
};

const sortChildPlaceSuggestionsForPlacement = (suggestions) =>
  orderChildPlaceSuggestionEntriesByHierarchy(
    normalizeChildPlaceSuggestions(suggestions)
    .map((suggestion, index) => ({ suggestion, index }))
    .sort((left, right) => {
      const priorityDelta = getChildPlacementPriority(left.suggestion) - getChildPlacementPriority(right.suggestion);
      return priorityDelta || left.index - right.index;
    })
  )
    .map((entry) => entry.suggestion);

const sortIndexedChildPlaceSuggestionsForPlacement = (entries) =>
  orderChildPlaceSuggestionEntriesByHierarchy(
    (Array.isArray(entries) ? entries : [])
    .map((entry, order) => ({ ...entry, order }))
    .sort((left, right) => {
      const priorityDelta = getChildPlacementPriority(left.suggestion) - getChildPlacementPriority(right.suggestion);
      return priorityDelta || left.index - right.index || left.order - right.order;
    })
  );

const normalizeSuggestionKeyText = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const createChildPlaceSuggestionKey = (suggestion) => {
  const name = normalizeSuggestionKeyText(suggestion?.name || suggestion?.savedPlaceName);
  const placeType = normalizeSuggestionKeyText(suggestion?.placeType);

  return name ? `${name}:${placeType}` : '';
};

const mergeChildPlaceSuggestions = (currentSuggestions, nextSuggestions) => {
  const mergedSuggestions = sortChildPlaceSuggestionsForPlacement(currentSuggestions);
  const seenKeys = new Set(mergedSuggestions.map(createChildPlaceSuggestionKey).filter(Boolean));

  normalizeChildPlaceSuggestions(nextSuggestions).forEach((suggestion) => {
    const key = createChildPlaceSuggestionKey(suggestion);
    if (key && seenKeys.has(key)) {
      return;
    }

    if (key) {
      seenKeys.add(key);
    }
    mergedSuggestions.push(suggestion);
  });

  return sortChildPlaceSuggestionsForPlacement(mergedSuggestions);
};

const getInhabitantRelationshipsForPlace = (suggestion, placeId) => {
  const normalizedPlaceId = String(placeId ?? '').trim();
  if (!normalizedPlaceId) {
    return [];
  }

  return normalizeInhabitantPlaceRelationships(suggestion?.placeRelationships)
    .filter((relationship) => relationship.targetPlaceId === normalizedPlaceId);
};

const getLinkedInhabitantSuggestions = (suggestions, placeId) =>
  (Array.isArray(suggestions) ? suggestions : [])
    .map((suggestion, index) => ({
      suggestion,
      index,
      relationships: getInhabitantRelationshipsForPlace(suggestion, placeId),
    }))
    .filter(({ suggestion, relationships }) =>
      String(suggestion?.memberOfPlaceId ?? '').trim() === String(placeId ?? '').trim() ||
      relationships.length > 0
    );

const buildTreeNodeSubtitle = (node) => {
  if (node.type === 'place') {
    return ['Place', node.placeType, node.zoneType, node.buildingType].filter(Boolean).join(' / ');
  }

  if (node.type === 'being') {
    return [
      'Inhabitant',
      node.title,
      node.beingType,
      node.level !== '' && node.level !== null && node.level !== undefined ? `Lvl ${node.level}` : '',
    ]
      .filter(Boolean)
      .join(' / ');
  }

  return [
    'Item',
    node.title,
    node.itemType,
    node.level !== '' && node.level !== null && node.level !== undefined ? `Lvl ${node.level}` : '',
  ]
    .filter(Boolean)
    .join(' / ');
};

function GenerateTownTreeNode({ node, depth, selectedNodeKey, onSelect }) {
  const [isOpen, setIsOpen] = useState(true);
  const children = Array.isArray(node.children) ? node.children : [];
  const hasChildren = children.length > 0;
  const subtitle = buildTreeNodeSubtitle(node);

  return (
    <div className={`place-tree-node ${depth > 0 ? 'child-node' : ''}`} style={{ '--tree-depth': depth }}>
      <button
        type="button"
        className={`place-tree-item explore-tree-item explore-tree-item-${node.type} ${
          selectedNodeKey === node.key ? 'active' : ''
        }`}
        onClick={() => onSelect(node.key)}
      >
        <span
          className="place-tree-caret"
          role={hasChildren ? 'button' : undefined}
          aria-label={hasChildren ? `${isOpen ? 'Collapse' : 'Expand'} ${node.name}` : undefined}
          onClick={(event) => {
            if (!hasChildren) {
              return;
            }

            event.stopPropagation();
            setIsOpen((current) => !current);
          }}
        >
          {hasChildren ? (isOpen ? 'v' : '>') : ''}
        </span>
        <span className="place-tree-labels">
          <span className="existing-list-name">{node.name}</span>
          {subtitle && <span className="existing-list-title">{subtitle}</span>}
        </span>
      </button>
      {hasChildren && isOpen && (
        <div className="place-tree-children">
          {children.map((child) => (
            <GenerateTownTreeNode
              key={child.key}
              node={child}
              depth={depth + 1}
              selectedNodeKey={selectedNodeKey}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const monitorPlaceTreeBackup = (result, setErrorMessage, persistenceErrorRef) => {
  const finish = completed => {
    if (completed.superseded) return;
    if (!completed.ok) {
      const message = completed.error?.message || 'The Place Tree draft could not be backed up.';
      if (persistenceErrorRef.current !== message) {
        persistenceErrorRef.current = message;
        setErrorMessage(`Place Tree session backup failed: ${message} Keep this tab open until the draft can be saved elsewhere.`);
      }
    } else if (persistenceErrorRef.current) {
      persistenceErrorRef.current = '';
      setErrorMessage(current => current.startsWith('Place Tree session backup failed:') ? '' : current);
    }
  };
  if (result.pending) result.pending.then(finish);
  else finish(result);
};

function GenerateTown({ placeOptions = [] }) {
  const [sessionInitialization, setSessionInitialization] = useState(() => {
    const restoredDraft = loadPlaceTreeSessionDraft();
    return {
      draft: restoredDraft ?? createEmptyPlaceTreeSessionDraft(),
      wasRestored: Boolean(restoredDraft),
      restoring: !restoredDraft && hasLargePlaceTreeSessionDraft(),
    };
  });
  const initialSessionDraft = sessionInitialization.draft;
  const [automation, setAutomation] = useState(() => ({ ...createPlaceTreeAutomation(), ...initialSessionDraft.automation }));
  const [recoverableBackups, setRecoverableBackups] = useState(null);
  const [isFindingBackups, setIsFindingBackups] = useState(false);
  const [prompt, setPrompt] = useState(initialSessionDraft.prompt);
  const [selectedParentPlaceId, setSelectedParentPlaceId] = useState(
    initialSessionDraft.selectedParentPlaceId
  );
  const [graph, setGraph] = useState(initialSessionDraft.graph);
  const [logEntries, setLogEntries] = useState(initialSessionDraft.logEntries);
  const [statusMessage, setStatusMessage] = useState(
    sessionInitialization.wasRestored
      ? 'Restored the Place Tree draft from this browser tab.'
      : ''
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [activeAction, setActiveAction] = useState('');
  const [lastRunSummary, setLastRunSummary] = useState(
    initialSessionDraft.lastRunSummary
  );
  const [selectedNodeKey, setSelectedNodeKey] = useState(
    initialSessionDraft.selectedNodeKey
  );
  const [draftPlace, setDraftPlace] = useState(initialSessionDraft.draftPlace);
  const [draftChildSuggestions, setDraftChildSuggestions] = useState(
    initialSessionDraft.draftChildSuggestions
  );
  const [draftInhabitantSuggestions, setDraftInhabitantSuggestions] = useState(
    initialSessionDraft.draftInhabitantSuggestions
  );
  const [draftHouseholdManifests, setDraftHouseholdManifests] = useState(
    initialSessionDraft.draftHouseholdManifests
  );
  const [
    draftCommercialInventoryManifest,
    setDraftCommercialInventoryManifest,
  ] = useState(initialSessionDraft.draftCommercialInventoryManifest);
  const [draftCommercialInventoryItems, setDraftCommercialInventoryItems] =
    useState(initialSessionDraft.draftCommercialInventoryItems);
  const [
    draftEstablishmentCurrencyHoldings,
    setDraftEstablishmentCurrencyHoldings,
  ] = useState(initialSessionDraft.draftEstablishmentCurrencyHoldings);
  const [draftNotableLoadoutManifest, setDraftNotableLoadoutManifest] =
    useState(initialSessionDraft.draftNotableLoadoutManifest);
  const [draftNotableLoadoutItems, setDraftNotableLoadoutItems] = useState(
    initialSessionDraft.draftNotableLoadoutItems
  );
  const [draftNotableCurrencyHoldings, setDraftNotableCurrencyHoldings] =
    useState(initialSessionDraft.draftNotableCurrencyHoldings);
  const [draftEvents, setDraftEvents] = useState(initialSessionDraft.draftEvents);
  const [worldDevelopment, setWorldDevelopment] = useState(initialSessionDraft.worldDevelopment);
  const [worldSaveProgress, setWorldSaveProgress] = useState(() =>
    initialSessionDraft.worldSaveProgress || createWorldSaveProgress());
  const [eventCount, setEventCount] = useState(8);
  const [isAutomaticStopRequested, setIsAutomaticStopRequested] = useState(false);
  const automaticStopRef = useRef(false);
  const [selectedPopulationTranche, setSelectedPopulationTranche] = useState(
    initialSessionDraft.selectedPopulationTranche
  );
  const [isPopulationGenerationStopRequested, setIsPopulationGenerationStopRequested] = useState(false);
  const [isInventoryGenerationStopRequested, setIsInventoryGenerationStopRequested] = useState(false);
  const [isNotableLoadoutStopRequested, setIsNotableLoadoutStopRequested] = useState(false);
  const [savedDraftPlaceId, setSavedDraftPlaceId] = useState(
    initialSessionDraft.savedDraftPlaceId
  );
  const [isDraftStructureLocked, setIsDraftStructureLocked] = useState(
    initialSessionDraft.isDraftStructureLocked
  );

  const graphRef = useRef(initialSessionDraft.graph);
  const sourcePromptRef = useRef(
    initialSessionDraft.sourcePrompt || initialSessionDraft.prompt
  );
  const populationGenerationStopRequestedRef = useRef(false);
  const inventoryGenerationStopRequestedRef = useRef(false);
  const notableLoadoutStopRequestedRef = useRef(false);
  const persistenceTimerRef = useRef(null);
  const persistenceErrorRef = useRef('');
  const latestSessionDraftRef = useRef(initialSessionDraft);

  const currentSessionDraft = {
    automation,
    prompt,
    selectedParentPlaceId,
    sourcePrompt: sourcePromptRef.current,
    graph,
    logEntries,
    lastRunSummary,
    selectedNodeKey,
    draftPlace,
    draftChildSuggestions,
    draftInhabitantSuggestions,
    draftHouseholdManifests,
    draftCommercialInventoryManifest,
    draftCommercialInventoryItems,
    draftEstablishmentCurrencyHoldings,
    draftNotableLoadoutManifest,
    draftNotableLoadoutItems,
    draftNotableCurrencyHoldings,
    draftEvents,
    worldSaveProgress,
    worldDevelopment,
    selectedPopulationTranche,
    savedDraftPlaceId,
    isDraftStructureLocked,
  };
  latestSessionDraftRef.current = currentSessionDraft;

  useEffect(() => {
    if (sessionInitialization.restoring) return;
    if (persistenceTimerRef.current) {
      clearTimeout(persistenceTimerRef.current);
    }
    // During saving, each acknowledged ID has its own small checkpoint.
    // Back up the full graph once the run stops rather than after every Item.
    if (['save-place-tree', 'automatic-place-tree', 'purge-place-tree'].includes(activeAction)) return;

    persistenceTimerRef.current = setTimeout(() => {
      const result = savePlaceTreeSessionDraft(latestSessionDraftRef.current);
      monitorPlaceTreeBackup(result, setErrorMessage, persistenceErrorRef);
    }, 300);

    return () => {
      if (persistenceTimerRef.current) {
        clearTimeout(persistenceTimerRef.current);
      }
    };
  }, [
    automation,
    activeAction,
    sessionInitialization.restoring,
    prompt,
    selectedParentPlaceId,
    graph,
    logEntries,
    lastRunSummary,
    selectedNodeKey,
    draftPlace,
    draftChildSuggestions,
    draftInhabitantSuggestions,
    draftHouseholdManifests,
    draftCommercialInventoryManifest,
    draftCommercialInventoryItems,
    draftEstablishmentCurrencyHoldings,
    draftNotableLoadoutManifest,
    draftNotableLoadoutItems,
    draftNotableCurrencyHoldings,
    draftEvents,
    worldSaveProgress,
    worldDevelopment,
    selectedPopulationTranche,
    savedDraftPlaceId,
    isDraftStructureLocked,
  ]);

  useEffect(
    () => {
      let cancelled = false;
      if (sessionInitialization.restoring) {
        (sessionInitialization.recoverBackupId
          ? recoverPlaceTreeBackup(sessionInitialization.recoverBackupId)
          : loadLargePlaceTreeSessionDraft()).then(restored => {
          if (cancelled) return;
          if (!restored) throw new Error('The Place Tree backup could not be found.');
          sourcePromptRef.current = restored.sourcePrompt || restored.prompt;
          graphRef.current = restored.graph;
          latestSessionDraftRef.current = restored;
          setPrompt(restored.prompt);
          setSelectedParentPlaceId(restored.selectedParentPlaceId);
          setGraph(restored.graph);
          setLogEntries(restored.logEntries);
          setLastRunSummary(restored.lastRunSummary);
          setSelectedNodeKey(restored.selectedNodeKey);
          setDraftPlace(restored.draftPlace);
          setDraftChildSuggestions(restored.draftChildSuggestions);
          setDraftInhabitantSuggestions(restored.draftInhabitantSuggestions);
          setDraftHouseholdManifests(restored.draftHouseholdManifests);
          setDraftCommercialInventoryManifest(restored.draftCommercialInventoryManifest);
          setDraftCommercialInventoryItems(restored.draftCommercialInventoryItems);
          setDraftEstablishmentCurrencyHoldings(restored.draftEstablishmentCurrencyHoldings);
          setDraftNotableLoadoutManifest(restored.draftNotableLoadoutManifest);
          setDraftNotableLoadoutItems(restored.draftNotableLoadoutItems);
          setDraftNotableCurrencyHoldings(restored.draftNotableCurrencyHoldings);
          setDraftEvents(restored.draftEvents);
          setWorldDevelopment(restored.worldDevelopment);
          setWorldSaveProgress(restored.worldSaveProgress || createWorldSaveProgress());
          setAutomation({ ...createPlaceTreeAutomation(), ...restored.automation });
          setSelectedPopulationTranche(restored.selectedPopulationTranche);
          setSavedDraftPlaceId(restored.savedDraftPlaceId);
          setIsDraftStructureLocked(restored.isDraftStructureLocked);
          setStatusMessage(sessionInitialization.recoverBackupId
            ? 'Recovered the complete Place Tree draft from stored browser backups.'
            : 'Restored the complete Place Tree draft from this browser tab.');
          setSessionInitialization(current => ({ ...current, restoring: false, restorationError: '' }));
        }).catch(error => {
          if (!cancelled) setSessionInitialization(current => ({ ...current, restorationError: error.message }));
        });
      }
      const flushSessionDraft = () => {
        if (sessionInitialization.restoring) return;
        if (persistenceTimerRef.current) {
          clearTimeout(persistenceTimerRef.current);
        }
        const backup = savePlaceTreeSessionDraft(latestSessionDraftRef.current);
        if (!cancelled) monitorPlaceTreeBackup(backup, setErrorMessage, persistenceErrorRef);
      };

      window.addEventListener('pagehide', flushSessionDraft);
      return () => {
        cancelled = true;
        window.removeEventListener('pagehide', flushSessionDraft);
        flushSessionDraft();
      };
  },
    [sessionInitialization.restoring, sessionInitialization.restorationAttempt, sessionInitialization.recoverBackupId]
  );

  if (sessionInitialization.restoring) return <section className="generator-panel">
    <p>{sessionInitialization.restorationError || 'Restoring the complete Place Tree draft…'}</p>
    {sessionInitialization.restorationError && <button type="button" onClick={() => setSessionInitialization(current => ({
      ...current, restorationError: '', restorationAttempt: (current.restorationAttempt || 0) + 1,
    }))}>Retry Draft Recovery</button>}
  </section>;

  const isBusy = Boolean(activeAction);

  const commitGraph = (nextGraph) => {
    graphRef.current = nextGraph;
    setGraph(nextGraph);
    return nextGraph;
  };

  const resetGraph = () => {
    const nextGraph = createEmptyGraph();
    graphRef.current = nextGraph;
    setGraph(nextGraph);
    setSelectedNodeKey('');
    return nextGraph;
  };

  const resetDraft = () => {
    setAutomation(current => ({ ...current, started: false, complete: false, stage: '' }));
    setWorldDevelopment(createEmptyWorldDevelopment());
    setDraftEvents([]);
    setWorldSaveProgress(createWorldSaveProgress());
    setDraftPlace(null);
    setDraftChildSuggestions([]);
    setDraftInhabitantSuggestions([]);
    setDraftHouseholdManifests({});
    setDraftCommercialInventoryManifest(null);
    setDraftCommercialInventoryItems([]);
    setDraftEstablishmentCurrencyHoldings([]);
    setDraftNotableLoadoutManifest(null);
    setDraftNotableLoadoutItems([]);
    setDraftNotableCurrencyHoldings([]);
    setSelectedPopulationTranche('notables');
    populationGenerationStopRequestedRef.current = false;
    setIsPopulationGenerationStopRequested(false);
    inventoryGenerationStopRequestedRef.current = false;
    setIsInventoryGenerationStopRequested(false);
    notableLoadoutStopRequestedRef.current = false;
    setIsNotableLoadoutStopRequested(false);
    setSavedDraftPlaceId('');
    setIsDraftStructureLocked(false);
  };

  const clearSessionDraft = ({ skipConfirmation = false } = {}) => {
    const confirmed = skipConfirmation || window.confirm(
      'Clear the entire in-tab Place Tree draft, including generated Places, residents, manifests, Items, Events, currencies, and progress? This cannot be undone. Records already saved to the database will not be deleted.'
    );
    if (!confirmed) {
      return;
    }

    const emptySessionDraft = createEmptyPlaceTreeSessionDraft();
    latestSessionDraftRef.current = emptySessionDraft;
    // Release the initial restored snapshot as well as the editable graph.
    // Refresh the lifecycle listener so it no longer retains that snapshot.
    setSessionInitialization(current => ({ draft: emptySessionDraft, wasRestored: false, restoring: false,
      restorationAttempt: (current.restorationAttempt || 0) + 1 }));
    const clearResult = clearPlaceTreeSessionDraft();
    monitorPlaceTreeBackup(clearResult, setErrorMessage, persistenceErrorRef);
    sourcePromptRef.current = '';
    setPrompt('');
    setSelectedParentPlaceId('');
    resetGraph();
    resetDraft();
    setLogEntries([]);
    setLastRunSummary(null);
    setErrorMessage(
      clearResult.ok
        ? ''
        : `The draft was cleared from the page, but session storage could not be cleared: ${clearResult.error?.message || 'Session storage is unavailable.'}`
    );
    setStatusMessage('Cleared the in-tab Place Tree draft.');
  };

  const purgeStoredDrafts = async () => {
    if (isBusy || isFindingBackups) return;
    if (!window.confirm('Purge all stored Place Tree drafts in this browser and clear the draft loaded in this tab? Unsaved work cannot be recovered. Records saved to the database will not be deleted. Other open tabs may create new backups.')) return;
    setActiveAction('purge-place-tree');
    clearSessionDraft({ skipConfirmation: true });
    setRecoverableBackups(null);
    setStatusMessage('Purging stored Place Tree drafts...');
    try {
      await purgePlaceTreeBackups();
      setStatusMessage('Purged stored Place Tree drafts and cleared the loaded draft. Saved database records are unchanged.');
    } catch (error) {
      setErrorMessage(`Could not purge all stored Place Tree drafts: ${error.message}`);
    } finally { setActiveAction(''); }
  };

  const addLogEntry = (message) => {
    setLogEntries((current) => [...current, `[${formatTimeStamp()}] ${message}`]);
  };

  const recordFailure = (runSummary, context, error) => {
    runSummary.failures += 1;
    addLogEntry(`${context}: failed. ${error?.message || 'Request failed.'}`);
  };

  const getExplorerPrompt = () => sourcePromptRef.current || prompt.trim();

  const withExplorerPrompt = (data = {}) => {
    const explorerPrompt = getExplorerPrompt();
    return explorerPrompt ? { ...data, prompt: explorerPrompt } : data;
  };

  const mergePlace = (currentGraph, place, options = {}) => {
    const placeId = place?.idEntity;
    if (!placeId) {
      return currentGraph;
    }

    const existingPlace = currentGraph.placesById[placeId] ?? {
      childPlaceIds: [],
      inhabitantIds: [],
      itemIds: [],
      parentPlaceId: '',
    };

    const nextPlacesById = {
      ...currentGraph.placesById,
      [placeId]: {
        ...existingPlace,
        idEntity: placeId,
        name: place?.name ?? place?.entityName ?? existingPlace.name ?? '',
        title: place?.title ?? place?.entityTitle ?? existingPlace.title ?? '',
        description: place?.description ?? place?.entityDesc ?? existingPlace.description ?? '',
        level: place?.level ?? existingPlace.level ?? '',
        placeType: place?.placeType ?? existingPlace.placeType ?? '',
        zoneType: place?.zoneType ?? existingPlace.zoneType ?? '',
        buildingType: place?.buildingType ?? existingPlace.buildingType ?? '',
        childPlaceIds: Array.isArray(existingPlace.childPlaceIds) ? existingPlace.childPlaceIds : [],
        inhabitantIds: Array.isArray(existingPlace.inhabitantIds) ? existingPlace.inhabitantIds : [],
        itemIds: Array.isArray(existingPlace.itemIds) ? existingPlace.itemIds : [],
        parentPlaceId: options.parentPlaceId ?? existingPlace.parentPlaceId ?? '',
      },
    };

    if (options.parentPlaceId) {
      const parentPlace = nextPlacesById[options.parentPlaceId] ?? {
        idEntity: options.parentPlaceId,
        childPlaceIds: [],
        inhabitantIds: [],
        itemIds: [],
      };

      nextPlacesById[options.parentPlaceId] = {
        ...parentPlace,
        childPlaceIds: appendUniqueId(parentPlace.childPlaceIds, placeId),
      };
    }

    return {
      ...currentGraph,
      rootPlaceId: options.setAsRoot ? placeId : currentGraph.rootPlaceId,
      placesById: nextPlacesById,
    };
  };

  const mergeItem = (currentGraph, item, options = {}) => {
    const itemId = item?.idEntity;
    if (!itemId) {
      return currentGraph;
    }

    const existingItem = currentGraph.itemsById[itemId] ?? {};
    const nextItemsById = {
      ...currentGraph.itemsById,
      [itemId]: {
        ...existingItem,
        idEntity: itemId,
        name: item?.name ?? item?.entityName ?? existingItem.name ?? '',
        title: item?.title ?? item?.entityTitle ?? existingItem.title ?? '',
        description: item?.description ?? item?.entityDesc ?? existingItem.description ?? '',
        level: item?.level ?? existingItem.level ?? '',
        itemType: item?.itemType ?? existingItem.itemType ?? '',
        ownerType: options.ownerType ?? existingItem.ownerType ?? '',
        ownerId: options.ownerId ?? existingItem.ownerId ?? '',
      },
    };

    const nextGraph = {
      ...currentGraph,
      itemsById: nextItemsById,
    };

    if (options.ownerType === 'place' && options.ownerId) {
      const ownerPlace = nextGraph.placesById[options.ownerId] ?? {
        idEntity: options.ownerId,
        childPlaceIds: [],
        inhabitantIds: [],
        itemIds: [],
      };

      nextGraph.placesById = {
        ...nextGraph.placesById,
        [options.ownerId]: {
          ...ownerPlace,
          itemIds: appendUniqueId(ownerPlace.itemIds, itemId),
        },
      };
    }

    if (options.ownerType === 'being' && options.ownerId) {
      const ownerBeing = nextGraph.beingsById[options.ownerId] ?? {
        idEntity: options.ownerId,
        itemIds: [],
      };

      nextGraph.beingsById = {
        ...nextGraph.beingsById,
        [options.ownerId]: {
          ...ownerBeing,
          itemIds: appendUniqueId(ownerBeing.itemIds, itemId),
        },
      };
    }

    return nextGraph;
  };

  const mergeBeing = (currentGraph, being, options = {}) => {
    const beingId = being?.idEntity;
    if (!beingId) {
      return currentGraph;
    }

    const existingBeing = currentGraph.beingsById[beingId] ?? {
      itemIds: [],
      placeId: '',
    };

    const nextBeingsById = {
      ...currentGraph.beingsById,
      [beingId]: {
        ...existingBeing,
        idEntity: beingId,
        name: being?.name ?? being?.entityName ?? existingBeing.name ?? '',
        title: being?.title ?? being?.entityTitle ?? existingBeing.title ?? '',
        description: being?.description ?? being?.entityDesc ?? existingBeing.description ?? '',
        level: being?.level ?? existingBeing.level ?? '',
        beingType: being?.beingType ?? existingBeing.beingType ?? '',
        alignment: being?.alignment ?? existingBeing.alignment ?? '',
        personality: being?.personality ?? existingBeing.personality ?? '',
        itemIds: Array.isArray(existingBeing.itemIds) ? existingBeing.itemIds : [],
        placeId: options.placeId ?? existingBeing.placeId ?? '',
      },
    };

    const nextGraph = {
      ...currentGraph,
      beingsById: nextBeingsById,
    };

    if (options.placeId) {
      const parentPlace = nextGraph.placesById[options.placeId] ?? {
        idEntity: options.placeId,
        childPlaceIds: [],
        inhabitantIds: [],
        itemIds: [],
      };

      nextGraph.placesById = {
        ...nextGraph.placesById,
        [options.placeId]: {
          ...parentPlace,
          inhabitantIds: appendUniqueId(parentPlace.inhabitantIds, beingId),
        },
      };
    }

    let updatedGraph = nextGraph;
    const ownedItems = Array.isArray(being?.ownedItems) ? being.ownedItems : [];
    ownedItems.forEach((item) => {
      updatedGraph = mergeItem(updatedGraph, item, { ownerType: 'being', ownerId: beingId });
    });

    return updatedGraph;
  };

  const ingestPlace = (currentGraph, place, options = {}) => {
    let nextGraph = mergePlace(currentGraph, place, options);
    const placeId = place?.idEntity;

    if (!placeId) {
      return nextGraph;
    }

    const composedOfChildren = Array.isArray(place?.composedOfChildren) ? place.composedOfChildren : [];
    composedOfChildren.forEach((childPlace) => {
      nextGraph = ingestPlace(nextGraph, childPlace, { parentPlaceId: placeId });
    });

    const savedInhabitants = Array.isArray(place?.savedInhabitants) ? place.savedInhabitants : [];
    savedInhabitants.forEach((being) => {
      nextGraph = mergeBeing(nextGraph, being, { placeId });
    });

    const containedItems = Array.isArray(place?.containedItems) ? place.containedItems : [];
    containedItems.forEach((item) => {
      nextGraph = mergeItem(nextGraph, item, { ownerType: 'place', ownerId: placeId });
    });

    return nextGraph;
  };

  const finishRun = (runSummary, message) => {
    setLastRunSummary(runSummary);
    setStatusMessage(message);
  };

  const commitDraftPlace = (place) => {
    setWorldDevelopment(createEmptyWorldDevelopment());
    setDraftEvents([]);
    setWorldSaveProgress(createWorldSaveProgress());
    const suggestions = sortChildPlaceSuggestionsForPlacement(place?.childPlaceSuggestions);
    const inhabitantSuggestions = normalizeInhabitantSuggestions(place?.inhabitantSuggestions);
    const childPlacePlan = normalizeChildPlacePlan(place?.childPlacePlan);
    const residentTranchePlan = normalizeResidentTranchePlan(place?.residentTranchePlan);
    const populationTrancheCounts = getPopulationTrancheCounts(inhabitantSuggestions);
    const expectedChildPlaceCount = resolveExpectedChildPlaceCount({
      expectedChildPlaceCount: place?.expectedChildPlaceCount,
      childPlacePlan,
    });
    setDraftPlace({
      ...(place ?? {}),
      expectedChildPlaceCount,
      expectedInhabitantCount: normalizeExpectedInhabitantCount(place?.expectedInhabitantCount),
      residentTranchePlan,
      plannedPopulation: normalizePlannedPopulation(place?.plannedPopulation),
      childPlacePlan,
      childPlaceSuggestions: suggestions,
      inhabitantSuggestions,
    });
    setDraftChildSuggestions(suggestions);
    setDraftInhabitantSuggestions(inhabitantSuggestions);
    setDraftHouseholdManifests({});
    setDraftCommercialInventoryManifest(null);
    setDraftCommercialInventoryItems([]);
    setDraftEstablishmentCurrencyHoldings([]);
    setDraftNotableLoadoutManifest(null);
    setDraftNotableLoadoutItems([]);
    setDraftNotableCurrencyHoldings([]);
    setSelectedPopulationTranche(
      residentTranchePlan.find(
        ({ tranche, expectedCount }) =>
          (populationTrancheCounts[tranche] ?? 0) < expectedCount
      )?.tranche || 'notables'
    );
    setSavedDraftPlaceId(place?.idEntity ?? '');
    setIsDraftStructureLocked(Boolean(place?.idEntity));
  };

  const runInitialTownGeneration = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      setErrorMessage('Prompt is required.');
      return;
    }

    setActiveAction('generate-town');
    setErrorMessage('');
    setStatusMessage('Generating place draft...');
    setLastRunSummary(null);
    setLogEntries([]);
    sourcePromptRef.current = trimmedPrompt;
    resetGraph();
    resetDraft();

    const runSummary = createRunSummary('Generate Place');

    try {
      addLogEntry('Generating unsaved place draft from prompt.');
      const generatedPlace = await callCdo(
        cdoPlace.generatePlace,
        {
          prompt: trimmedPrompt,
          aogYear: DEFAULT_AOG_YEAR,
          parentIdEntity: selectedParentPlaceId || '',
        }
      );
      commitDraftPlace({
        ...(generatedPlace ?? {}),
        parentIdEntity: generatedPlace?.parentIdEntity ?? selectedParentPlaceId ?? '',
      });
      const suggestions = sortChildPlaceSuggestionsForPlacement(generatedPlace?.childPlaceSuggestions);
      const expectedChildPlaceCount = resolveExpectedChildPlaceCount(generatedPlace);
      const expectedInhabitantCount = normalizeExpectedInhabitantCount(
        generatedPlace?.expectedInhabitantCount
      );
      const residentTranchePlan = normalizeResidentTranchePlan(
        generatedPlace?.residentTranchePlan
      );
      const plannedPopulation = normalizePlannedPopulation(generatedPlace?.plannedPopulation);
      addLogEntry(`Generated unsaved place draft ${getPlaceLabel(generatedPlace)}.`);
      addLogEntry(`Place draft planned population: ${formatPlannedPopulation(plannedPopulation)}.`);
      if (expectedChildPlaceCount > 0) {
        addLogEntry(`Place draft expects about ${expectedChildPlaceCount} direct sub-place${expectedChildPlaceCount === 1 ? '' : 's'} when complete.`);
      }
      if (expectedInhabitantCount > 0) {
        addLogEntry(`Place draft expects about ${expectedInhabitantCount} notable inhabitant${expectedInhabitantCount === 1 ? '' : 's'} when the cast is complete.`);
      }
      if (residentTranchePlan.length > 0) {
        addLogEntry(`Population plan covers ${getResidentTranchePlanTotal(residentTranchePlan)} permanent resident${getResidentTranchePlanTotal(residentTranchePlan) === 1 ? '' : 's'} across ${residentTranchePlan.length} tranches.`);
      }
      if (suggestions.length > 0) {
        addLogEntry(`Place draft included ${suggestions.length} sub-place suggestion${suggestions.length === 1 ? '' : 's'}.`);
      }

      finishRun(
        runSummary,
        `Generated unsaved place draft ${getPlaceLabel(generatedPlace)}. Planned population: ${formatPlannedPopulation(plannedPopulation)}.${expectedChildPlaceCount > 0 ? ` Planned sub-places: ${expectedChildPlaceCount}.` : ''}${expectedInhabitantCount > 0 ? ` Planned notable inhabitants: ${expectedInhabitantCount}.` : ''}${suggestions.length > 0 ? ` ${suggestions.length} sub-place suggestion(s) are ready for review.` : ''}`
      );
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to generate place.');
      setStatusMessage('Place generation stopped.');
      setLastRunSummary(runSummary);
    } finally {
      setActiveAction('');
    }
  };

  const runDraftSubPlaceSuggestionPass = async (options = {}) => {
    if (isBusy) return;
    if (!draftPlace) {
      setErrorMessage('Generate a place draft before suggesting sub-places.');
      return;
    }

    if (isDraftStructureLocked) {
      setErrorMessage('The place tree is already being saved and its structure can no longer be regenerated.');
      return;
    }

    const shouldReplace = Boolean(options?.replace);
    const shouldCreateAll = Boolean(options?.all);
    let existingSuggestions = shouldReplace ? [] : draftChildSuggestions;
    const expectedChildPlaceCount = resolveExpectedChildPlaceCount(draftPlace);
    const childPlacePlan = normalizeChildPlacePlan(draftPlace?.childPlacePlan);
    if (shouldCreateAll && (expectedChildPlaceCount <= 0 || existingSuggestions.length >= expectedChildPlaceCount)) return;
    let totalAdded = 0;

    setActiveAction(shouldCreateAll ? 'suggest-all-subplaces' : 'suggest-subplaces');
    setErrorMessage('');
    setStatusMessage(shouldReplace ? 'Refreshing sub-place suggestions...' : 'Suggesting next sub-place batch...');
    const runSummary = createRunSummary(shouldCreateAll ? 'Create All SubPlaces' : shouldReplace ? 'Refresh Sub-Places' : 'Suggest Sub-Places');

    try {
      do {
        const requestedCount = shouldCreateAll
          ? Math.min(CHILD_SUGGESTION_BATCH_SIZE, expectedChildPlaceCount - existingSuggestions.length)
          : CHILD_SUGGESTION_BATCH_SIZE;
        setStatusMessage(shouldCreateAll
          ? `Creating sub-places: ${existingSuggestions.length}/${expectedChildPlaceCount} drafted. Requesting next ${requestedCount}...`
          : shouldReplace ? 'Refreshing sub-place suggestions...' : 'Suggesting next sub-place batch...');
        const requestPayload = savedDraftPlaceId
          ? { idEntity: savedDraftPlaceId, aogYear: DEFAULT_AOG_YEAR }
          : { place: { ...draftPlace, childPlaceSuggestions: existingSuggestions }, aogYear: DEFAULT_AOG_YEAR };
        const plannedRequestPayload = {
          ...requestPayload,
          requestedCount,
          expectedChildPlaceCount,
          childPlacePlan,
          existingChildPlaceSuggestions: existingSuggestions,
          promptRole: 'original_place_prompt',
        };
        addLogEntry(
          `${getPlaceLabel(draftPlace)}: requesting ${shouldReplace ? 'replacement' : 'next'} sub-place batch${expectedChildPlaceCount > 0 ? ` (${existingSuggestions.length}/${expectedChildPlaceCount} already drafted)` : ''}.`
        );
        const response = await callCdo(
          cdoPlace.generateChildSuggestions,
          withExplorerPrompt(plannedRequestPayload)
        );
        const returnedSuggestions = sortChildPlaceSuggestionsForPlacement(response?.childPlaceSuggestions);
        const suggestions = shouldReplace
          ? returnedSuggestions
          : mergeChildPlaceSuggestions(existingSuggestions, returnedSuggestions);
        const addedCount = suggestions.length - existingSuggestions.length;
        if (shouldCreateAll && addedCount <= 0) {
          throw new Error(`No new sub-places returned. Stopped at ${existingSuggestions.length} of ${expectedChildPlaceCount}; existing drafts are kept. Use More to retry.`);
        }
        totalAdded += shouldReplace ? suggestions.length : addedCount;
        setDraftPlace((current) => ({
          ...(current ?? draftPlace),
          expectedChildPlaceCount,
          childPlacePlan,
          childPlaceSuggestions: suggestions,
          inhabitantSuggestions: [],
        }));
        setDraftChildSuggestions(suggestions);
        setDraftInhabitantSuggestions([]);
        setDraftHouseholdManifests({});
        setDraftCommercialInventoryManifest(null);
        setDraftCommercialInventoryItems([]);
        applyEstablishmentCurrencyHoldings([]);
        setDraftNotableLoadoutManifest(null);
        setDraftNotableLoadoutItems([]);
        setDraftNotableCurrencyHoldings([]);
        addLogEntry(
          `${getPlaceLabel(draftPlace)}: ${returnedSuggestions.length} sub-place suggestion${returnedSuggestions.length === 1 ? '' : 's'} returned; ${suggestions.length}${expectedChildPlaceCount > 0 ? `/${expectedChildPlaceCount}` : ''} drafted.`
        );
        existingSuggestions = suggestions;
      } while (shouldCreateAll && existingSuggestions.length < expectedChildPlaceCount);
      finishRun(
        runSummary,
        `${shouldCreateAll ? 'Created' : 'Suggested'} ${totalAdded} sub-place${totalAdded === 1 ? '' : 's'}. ${existingSuggestions.length}${expectedChildPlaceCount > 0 ? ` of ${expectedChildPlaceCount}` : ''} drafted.`
      );
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to suggest sub-places.');
      setStatusMessage('Sub-place suggestion stopped.');
      setLastRunSummary(runSummary);
    } finally {
      setActiveAction('');
    }
  };

  const requestDraftCommercialInventoryManifest = async () => {
    const commercialPlaces = getInventoryDraftPlaces(
      draftPlace,
      draftChildSuggestions
    );
    const response = await callCdo(
      cdoPlace.generateCommercialInventoryManifest,
      withExplorerPrompt({
        place: {
          ...draftPlace,
          childPlaceSuggestions: draftChildSuggestions,
          inhabitantSuggestions: draftInhabitantSuggestions,
        },
        existingInhabitantSuggestions: draftInhabitantSuggestions,
        commercialPlaces,
        aogYear: DEFAULT_AOG_YEAR,
      })
    );
    const manifest = response?.commercialInventoryManifest;

    validateCommercialInventoryManifest(manifest, commercialPlaces);
    return manifest;
  };

  const applyEstablishmentCurrencyHoldings = (holdings) => {
    const normalizedHoldings = Array.isArray(holdings) ? holdings : [];
    const holdingsByPlaceId = new Map(
      normalizedHoldings.map((holding) => [
        String(holding?.commercialPlaceId ?? '').trim(),
        holding,
      ])
    );
    const attachHolding = (place, fallbackId = '') => {
      const placeId = String(
        place?.draftPlaceId ?? place?.idEntity ?? fallbackId
      ).trim();
      const holding = holdingsByPlaceId.get(placeId);
      if (!holding) {
        const placeWithoutGeneratedCurrency = { ...(place ?? {}) };
        delete placeWithoutGeneratedCurrency.currencies;
        delete placeWithoutGeneratedCurrency.inventoryRetailValueGp;
        delete placeWithoutGeneratedCurrency.currencyReserveRatio;
        delete placeWithoutGeneratedCurrency.currencyTargetGp;
        delete placeWithoutGeneratedCurrency.currencyActualGp;
        return placeWithoutGeneratedCurrency;
      }
      return {
        ...place,
        currencies: holding.currencies,
        inventoryRetailValueGp: holding.inventoryRetailValueGp,
        currencyReserveRatio: holding.cashReserveRatio,
        currencyTargetGp: holding.targetCashGp,
        currencyActualGp: holding.actualCashGp,
      };
    };

    setDraftEstablishmentCurrencyHoldings(normalizedHoldings);
    setDraftPlace((current) =>
      current ? attachHolding(current, 'draft-place:root') : current
    );
    setDraftChildSuggestions((current) =>
      current.map((place) => attachHolding(place))
    );
  };

  const runDraftCommercialInventoryPlan = async ({ replace = false } = {}) => {
    if (!draftPlace) {
      setErrorMessage('Generate a place draft before planning trade inventory.');
      return;
    }
    if (!isSubPlacePlanComplete) {
      setErrorMessage('Complete the planned sub-places before planning trade inventory.');
      return;
    }
    if (isDraftStructureLocked) {
      setErrorMessage('Trade inventory planning only operates on an unsaved place draft.');
      return;
    }

    const commercialPlaces = getInventoryDraftPlaces(
      draftPlace,
      draftChildSuggestions
    );
    if (commercialPlaces.length === 0) {
      setErrorMessage(
        'This place draft has no Commercial, Industrial, or Agricultural ' +
        'places to evaluate for trade inventory.'
      );
      return;
    }
    if (replace && draftCommercialInventoryItems.length > 0) {
      setErrorMessage('An inventory plan cannot be replaced after Item generation has started.');
      return;
    }

    const runSummary = createRunSummary(
      replace ? 'Regenerate Trade Inventory Plan' : 'Plan Trade Inventories'
    );
    setActiveAction('plan-commercial-inventory');
    setErrorMessage('');
    setStatusMessage(
      `Evaluating trade stock for ${commercialPlaces.length} Commercial, Industrial, and Agricultural place${commercialPlaces.length === 1 ? '' : 's'}...`
    );

    try {
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: requesting trade inventory decisions, types, and exact Item counts for ${commercialPlaces.length} candidate place${commercialPlaces.length === 1 ? '' : 's'}.`
      );
      const manifest = await requestDraftCommercialInventoryManifest();
      const plannedItemCount = getCommercialInventoryProgress(
        manifest,
        []
      ).targetCount;
      setDraftCommercialInventoryManifest(manifest);
      setDraftCommercialInventoryItems([]);
      applyEstablishmentCurrencyHoldings([]);
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: planned ${plannedItemCount} trade inventory Item drafts across ${manifest.commercialPlaceInventories.length} evaluated place${manifest.commercialPlaceInventories.length === 1 ? '' : 's'}.`
      );
      finishRun(
        runSummary,
        `Trade inventory plan is ready for review: ${plannedItemCount} Item drafts across ${manifest.commercialPlaceInventories.length} evaluated places.`
      );
    } catch (error) {
      const failureMessage = error?.message || 'Failed to plan trade inventory.';
      setErrorMessage(failureMessage);
      setStatusMessage('Trade inventory planning stopped.');
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: trade inventory plan failed: ${failureMessage}`
      );
      setLastRunSummary(runSummary);
    } finally {
      setActiveAction('');
    }
  };

  const requestDraftCommercialInventoryBatch = async ({
    requestedSlots,
    currentItemDrafts,
  }) => {
    const response = await callCdo(
      cdoPlace.generateCommercialInventoryItems,
      withExplorerPrompt({
        place: {
          ...draftPlace,
          childPlaceSuggestions: draftChildSuggestions,
          inhabitantSuggestions: draftInhabitantSuggestions,
        },
        existingInhabitantSuggestions: draftInhabitantSuggestions,
        commercialPlaces: getInventoryDraftPlaces(
          draftPlace,
          draftChildSuggestions
        ),
        commercialInventoryManifest: draftCommercialInventoryManifest,
        requestedInventorySlotIds: requestedSlots.map(
          (slot) => slot.inventorySlotId
        ),
        existingInventoryItemDrafts: [
          ...draftNotableLoadoutItems,
          ...currentItemDrafts,
        ],
        aogYear: DEFAULT_AOG_YEAR,
      })
    );

    applyEstablishmentCurrencyHoldings(
      response?.establishmentCurrencyHoldings
    );
    return Array.isArray(response?.itemDrafts) ? response.itemDrafts : [];
  };

  const runDraftCommercialInventorySeries = async ({
    singleBatch = false,
  } = {}) => {
    if (!draftCommercialInventoryManifest) {
      setErrorMessage('Plan trade inventories before generating Items.');
      return;
    }
    if (isDraftStructureLocked) {
      setErrorMessage('Trade inventory generation only operates on an unsaved place draft.');
      return;
    }

    const nextBatch = getNextCommercialInventoryBatch(
      draftCommercialInventoryManifest,
      draftCommercialInventoryItems,
      COMMERCIAL_INVENTORY_BATCH_SIZE
    );
    if (!nextBatch) {
      setErrorMessage('The planned trade inventory is complete.');
      return;
    }

    const runSummary = createRunSummary(
      singleBatch ? 'Generate Next Trade Item Batch' : 'Generate All Trade Items'
    );
    const startingItemCount = draftCommercialInventoryItems.length;
    let latestItemDrafts = draftCommercialInventoryItems;
    let completedBatchCount = 0;

    inventoryGenerationStopRequestedRef.current = false;
    setIsInventoryGenerationStopRequested(false);
    setActiveAction(
      singleBatch
        ? 'generate-commercial-inventory-batch'
        : 'generate-commercial-inventory'
    );
    setErrorMessage('');
    setStatusMessage(
      singleBatch
        ? `Generating the next batch of up to ${COMMERCIAL_INVENTORY_BATCH_SIZE} trade Items...`
        : 'Generating all remaining trade Items in serial batches...'
    );

    try {
      const result = await generateCommercialInventoryBatchesSerially({
        manifest: draftCommercialInventoryManifest,
        itemDrafts: draftCommercialInventoryItems,
        batchSize: COMMERCIAL_INVENTORY_BATCH_SIZE,
        shouldContinue: () =>
          !inventoryGenerationStopRequestedRef.current &&
          (!singleBatch || completedBatchCount === 0),
        requestBatch: async ({
          requestedSlots,
          currentItemDrafts,
          batchNumber,
        }) => {
          const progress = getCommercialInventoryProgress(
            draftCommercialInventoryManifest,
            currentItemDrafts
          );
          const placeNames = [
            ...new Set(
              requestedSlots.map((slot) => slot.commercialPlaceName)
            ),
          ].join(', ');
          setStatusMessage(
            `Generating trade inventory batch ${batchNumber} (${progress.generatedCount}/${progress.targetCount})...`
          );
          addLogEntry(
            `${getPlaceLabel(draftPlace)}: requesting serial inventory batch ${batchNumber} for ${placeNames} (${progress.generatedCount}/${progress.targetCount} Item drafts already generated).`
          );
          return requestDraftCommercialInventoryBatch({
            requestedSlots,
            currentItemDrafts,
          });
        },
        onBatch: ({
          returnedItemDrafts,
          itemDrafts,
          batchNumber,
        }) => {
          const progress = getCommercialInventoryProgress(
            draftCommercialInventoryManifest,
            itemDrafts
          );
          latestItemDrafts = itemDrafts;
          completedBatchCount = batchNumber;
          setDraftCommercialInventoryItems(itemDrafts);
          addLogEntry(
            `${getPlaceLabel(draftPlace)}: serial inventory batch ${batchNumber} returned ${returnedItemDrafts.length} complete Item drafts; ${progress.generatedCount}/${progress.targetCount} generated in memory.`
          );
        },
      });
      const progress = getCommercialInventoryProgress(
        draftCommercialInventoryManifest,
        result.itemDrafts
      );

      latestItemDrafts = result.itemDrafts;
      completedBatchCount = result.batchCount;
      setDraftCommercialInventoryItems(result.itemDrafts);
      finishRun(
        runSummary,
        singleBatch
          ? `Generated ${result.itemDrafts.length - startingItemCount} Item drafts for review; ${progress.generatedCount} of ${progress.targetCount} trade Items are now generated in memory.`
          : result.stopped
          ? `Stopped after ${result.batchCount} serial inventory batch${result.batchCount === 1 ? '' : 'es'}; ${progress.generatedCount} of ${progress.targetCount} Item drafts remain in memory.`
          : `Generated all ${progress.generatedCount} trade Item drafts in ${result.batchCount} serial batch${result.batchCount === 1 ? '' : 'es'}.`
      );
    } catch (error) {
      const progress = getCommercialInventoryProgress(
        draftCommercialInventoryManifest,
        latestItemDrafts
      );
      setErrorMessage(error?.message || 'Failed to generate trade inventory.');
      setStatusMessage(
        singleBatch
          ? `Trade inventory batch generation stopped; ${progress.generatedCount} of ${progress.targetCount} Item drafts remain in memory.`
          : `Trade inventory generation stopped after ${completedBatchCount} completed serial batch${completedBatchCount === 1 ? '' : 'es'}; ${progress.generatedCount} of ${progress.targetCount} Item drafts remain in memory.`
      );
      setLastRunSummary(runSummary);
    } finally {
      inventoryGenerationStopRequestedRef.current = false;
      setIsInventoryGenerationStopRequested(false);
      setActiveAction('');
    }
  };

  const stopInventoryGenerationAfterCurrentBatch = () => {
    inventoryGenerationStopRequestedRef.current = true;
    setIsInventoryGenerationStopRequested(true);
    setStatusMessage('Stopping inventory generation after the current batch returns...');
  };

  const commitDraftInhabitantSuggestions = (suggestions) => {
    setDraftInhabitantSuggestions(suggestions);
    setDraftPlace((current) =>
      current
        ? {
            ...current,
            inhabitantSuggestions: suggestions,
          }
        : current
    );
  };

  const commitDraftHouseholdManifest = (manifest) => {
    const normalizedManifest = normalizeHouseholdManifest(manifest);
    if (!normalizedManifest) {
      throw new Error('The household planner returned an invalid manifest.');
    }

    setDraftHouseholdManifests((current) => ({
      ...current,
      [normalizedManifest.populationTranche]: normalizedManifest,
    }));
    return normalizedManifest;
  };

  const clearDraftHouseholdManifestsFrom = (populationTranche) => {
    setDraftHouseholdManifests((current) =>
      invalidateHouseholdManifestsFrom(current, populationTranche)
    );
  };

  const clearDraftNotableLoadouts = (residents = draftInhabitantSuggestions) => {
    const residentsWithoutLoadoutCurrency = (Array.isArray(residents) ? residents : [])
      .map((resident) => {
        if (normalizePopulationTranche(resident?.populationTranche) !== 'notables') {
          return resident;
        }
        const residentWithoutCurrency = { ...resident };
        delete residentWithoutCurrency.currencies;
        delete residentWithoutCurrency.currencyTargetGp;
        delete residentWithoutCurrency.currencyActualGp;
        return residentWithoutCurrency;
      });
    setDraftNotableLoadoutManifest(null);
    setDraftNotableLoadoutItems([]);
    setDraftNotableCurrencyHoldings([]);
    commitDraftInhabitantSuggestions(residentsWithoutLoadoutCurrency);
    return residentsWithoutLoadoutCurrency;
  };

  const runDraftNotableLoadoutPlan = async ({ replace = false } = {}) => {
    if (!draftPlace) {
      setErrorMessage('Generate a place draft before planning notable loadouts.');
      return;
    }
    if (!isSubPlacePlanComplete || isDraftStructureLocked) {
      setErrorMessage(
        isDraftStructureLocked
          ? 'Notable loadout planning only operates on an unsaved place draft.'
          : 'Complete the planned sub-places before planning notable loadouts.'
      );
      return;
    }
    const notableTarget = normalizeResidentTranchePlan(
      draftPlace?.residentTranchePlan
    ).find(({ tranche }) => tranche === 'notables')?.expectedCount ?? 0;
    const notableCount = getPopulationTrancheCounts(
      draftInhabitantSuggestions
    ).notables ?? 0;
    if (notableTarget < 1 || notableCount !== notableTarget) {
      setErrorMessage('Complete the notable population tranche before planning loadouts.');
      return;
    }
    if (replace && draftNotableLoadoutItems.length > 0) {
      setErrorMessage('A loadout plan cannot be replaced after Item generation has started.');
      return;
    }

    const runSummary = createRunSummary(
      replace ? 'Regenerate Notable Loadout Plan' : 'Plan Notable Loadouts'
    );
    setActiveAction('plan-notable-loadouts');
    setErrorMessage('');
    setStatusMessage(
      `Planning key possessions and personal currency for ${notableCount} notables...`
    );

    try {
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: requesting one loadout plan for each of ${notableCount} notable residents.`
      );
      const response = await callCdo(
        cdoPlace.generateNotableLoadoutManifest,
        withExplorerPrompt({
          place: {
            ...draftPlace,
            childPlaceSuggestions: draftChildSuggestions,
            inhabitantSuggestions: draftInhabitantSuggestions,
          },
          existingInhabitantSuggestions: draftInhabitantSuggestions,
          aogYear: DEFAULT_AOG_YEAR,
        })
      );
      const manifest = validateNotableLoadoutManifest(
        normalizeNotableLoadoutManifest(response?.notableLoadoutManifest),
        draftInhabitantSuggestions
      );
      const currencyHoldings = Array.isArray(response?.notableCurrencyHoldings)
        ? response.notableCurrencyHoldings
        : [];
      const residentsWithCurrency = attachNotableCurrencyHoldings(
        draftInhabitantSuggestions,
        currencyHoldings
      );
      setDraftNotableLoadoutManifest(manifest);
      setDraftNotableLoadoutItems([]);
      setDraftNotableCurrencyHoldings(currencyHoldings);
      commitDraftInhabitantSuggestions(residentsWithCurrency);
      const progress = getNotableLoadoutProgress(manifest, []);
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: planned ${progress.targetCount} notable Item slots and ${currencyHoldings.length} personal currency holdings.`
      );
      finishRun(
        runSummary,
        `Notable loadout plan is ready for review: ${progress.targetCount} key Item drafts across ${notableCount} notables.`
      );
    } catch (error) {
      const failureMessage = error?.message || 'Failed to plan notable loadouts.';
      setErrorMessage(failureMessage);
      setStatusMessage('Notable loadout planning stopped.');
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: notable loadout plan failed: ${failureMessage}`
      );
      setLastRunSummary(runSummary);
    } finally {
      setActiveAction('');
    }
  };

  const requestDraftNotableLoadoutBatch = async ({
    requestedSlots,
    currentItemDrafts,
  }) => {
    const response = await callCdo(
      cdoPlace.generateNotableLoadoutItems,
      withExplorerPrompt({
        place: {
          ...draftPlace,
          childPlaceSuggestions: draftChildSuggestions,
          inhabitantSuggestions: draftInhabitantSuggestions,
        },
        existingInhabitantSuggestions: draftInhabitantSuggestions,
        notableLoadoutManifest: draftNotableLoadoutManifest,
        requestedNotableItemSlotIds: requestedSlots.map(
          (slot) => slot.notableItemSlotId
        ),
        existingNotableItemDrafts: [
          ...draftCommercialInventoryItems,
          ...currentItemDrafts,
        ],
        aogYear: DEFAULT_AOG_YEAR,
      })
    );
    return Array.isArray(response?.itemDrafts) ? response.itemDrafts : [];
  };

  const runDraftNotableLoadoutSeries = async ({ singleBatch = false } = {}) => {
    if (!draftNotableLoadoutManifest) {
      setErrorMessage('Plan notable loadouts before generating Items.');
      return;
    }
    if (isDraftStructureLocked) {
      setErrorMessage('Notable Item generation only operates on an unsaved place draft.');
      return;
    }
    if (!getNextNotableLoadoutBatch(
      draftNotableLoadoutManifest,
      draftNotableLoadoutItems,
      NOTABLE_LOADOUT_BATCH_SIZE
    )) {
      setErrorMessage('The planned notable loadouts are complete.');
      return;
    }

    const runSummary = createRunSummary(
      singleBatch ? 'Generate Next Notable Item Batch' : 'Generate All Notable Items'
    );
    const startingItemCount = draftNotableLoadoutItems.length;
    let latestItemDrafts = draftNotableLoadoutItems;
    let completedBatchCount = 0;
    notableLoadoutStopRequestedRef.current = false;
    setIsNotableLoadoutStopRequested(false);
    setActiveAction(
      singleBatch ? 'generate-notable-loadout-batch' : 'generate-notable-loadouts'
    );
    setErrorMessage('');
    setStatusMessage(
      singleBatch
        ? `Generating the next batch of up to ${NOTABLE_LOADOUT_BATCH_SIZE} notable Items...`
        : 'Generating all remaining notable Items in serial batches...'
    );

    try {
      const result = await generateNotableLoadoutBatchesSerially({
        manifest: draftNotableLoadoutManifest,
        itemDrafts: draftNotableLoadoutItems,
        batchSize: NOTABLE_LOADOUT_BATCH_SIZE,
        shouldContinue: () =>
          !notableLoadoutStopRequestedRef.current &&
          (!singleBatch || completedBatchCount === 0),
        requestBatch: async ({
          requestedSlots,
          currentItemDrafts,
          batchNumber,
        }) => {
          const progress = getNotableLoadoutProgress(
            draftNotableLoadoutManifest,
            currentItemDrafts
          );
          setStatusMessage(
            `Generating notable Item batch ${batchNumber} (${progress.generatedCount}/${progress.targetCount})...`
          );
          addLogEntry(
            `${getPlaceLabel(draftPlace)}: requesting serial notable Item batch ${batchNumber} (${progress.generatedCount}/${progress.targetCount} already generated).`
          );
          return requestDraftNotableLoadoutBatch({
            requestedSlots,
            currentItemDrafts,
          });
        },
        onBatch: ({ returnedItemDrafts, itemDrafts, batchNumber }) => {
          const progress = getNotableLoadoutProgress(
            draftNotableLoadoutManifest,
            itemDrafts
          );
          latestItemDrafts = itemDrafts;
          completedBatchCount = batchNumber;
          setDraftNotableLoadoutItems(itemDrafts);
          addLogEntry(
            `${getPlaceLabel(draftPlace)}: notable Item batch ${batchNumber} returned ${returnedItemDrafts.length} complete Item drafts; ${progress.generatedCount}/${progress.targetCount} generated in memory.`
          );
        },
      });
      const progress = getNotableLoadoutProgress(
        draftNotableLoadoutManifest,
        result.itemDrafts
      );
      setDraftNotableLoadoutItems(result.itemDrafts);
      finishRun(
        runSummary,
        singleBatch
          ? `Generated ${result.itemDrafts.length - startingItemCount} notable Item drafts for review; ${progress.generatedCount} of ${progress.targetCount} are complete.`
          : result.stopped
            ? `Stopped after ${result.batchCount} notable Item batch${result.batchCount === 1 ? '' : 'es'}; ${progress.generatedCount} of ${progress.targetCount} remain in memory.`
            : `Generated all ${progress.generatedCount} notable Item drafts in ${result.batchCount} serial batch${result.batchCount === 1 ? '' : 'es'}.`
      );
    } catch (error) {
      const progress = getNotableLoadoutProgress(
        draftNotableLoadoutManifest,
        latestItemDrafts
      );
      setErrorMessage(error?.message || 'Failed to generate notable Items.');
      setStatusMessage(
        `Notable Item generation stopped after ${completedBatchCount} completed batch${completedBatchCount === 1 ? '' : 'es'}; ${progress.generatedCount} of ${progress.targetCount} remain in memory.`
      );
      setLastRunSummary(runSummary);
    } finally {
      notableLoadoutStopRequestedRef.current = false;
      setIsNotableLoadoutStopRequested(false);
      setActiveAction('');
    }
  };

  const stopNotableLoadoutAfterCurrentBatch = () => {
    notableLoadoutStopRequestedRef.current = true;
    setIsNotableLoadoutStopRequested(true);
    setStatusMessage('Stopping notable Item generation after the current batch returns...');
  };

  const requestDraftHouseholdManifest = async ({
    populationTranche,
    targetCount,
    currentSuggestions = draftInhabitantSuggestions,
  }) => {
    const response = await callCdo(
      cdoPlace.generateHouseholdManifest,
      withExplorerPrompt({
        place: {
          ...draftPlace,
          childPlaceSuggestions: draftChildSuggestions,
          inhabitantSuggestions: currentSuggestions,
        },
        existingInhabitantSuggestions: currentSuggestions,
        populationTranche,
        targetCount,
        aogYear: DEFAULT_AOG_YEAR,
      })
    );
    const manifest = normalizeHouseholdManifest(response?.householdManifest);

    if (
      !manifest ||
      manifest.populationTranche !== populationTranche ||
      manifest.targetCount !== targetCount
    ) {
      throw new Error(
        `The household planner did not return a valid ${populationTranche} manifest.`
      );
    }

    return manifest;
  };

  const runDraftHouseholdManifestPlan = async ({
    populationTranche = selectedPopulationTranche,
    currentSuggestions = draftInhabitantSuggestions,
    replace = false,
  } = {}) => {
    if (!draftPlace) {
      setErrorMessage('Generate a place draft before planning its households.');
      return;
    }

    const normalizedTranche = normalizePopulationTranche(populationTranche);
    if (!isHouseholdManifestTranche(normalizedTranche)) {
      setErrorMessage('Select a household population tranche.');
      return;
    }

    const residentTranchePlan = normalizeResidentTranchePlan(
      draftPlace?.residentTranchePlan
    );
    const tranchePlanEntry = residentTranchePlan.find(
      ({ tranche }) => tranche === normalizedTranche
    );
    const currentCount = getPopulationTrancheCounts(currentSuggestions)[normalizedTranche] ?? 0;
    const canPlan = canGeneratePopulationTranche(
      normalizedTranche,
      residentTranchePlan,
      currentSuggestions
    );

    if (!tranchePlanEntry || !canPlan) {
      setErrorMessage(
        currentCount >= (tranchePlanEntry?.expectedCount ?? 0)
          ? `${POPULATION_TRANCHES.find(({ key }) => key === normalizedTranche)?.label || 'This tranche'} is complete.`
          : 'Complete the preceding population tranche first.'
      );
      return;
    }
    if (currentCount > 0) {
      setErrorMessage('Refresh the household tranche before replacing its manifest.');
      return;
    }

    const trancheLabel = POPULATION_TRANCHES.find(
      ({ key }) => key === normalizedTranche
    )?.label || normalizedTranche;
    const runSummary = createRunSummary(
      replace ? `Regenerate ${trancheLabel} Plan` : `Plan ${trancheLabel}`
    );

    setActiveAction('plan-households');
    setErrorMessage('');
    setStatusMessage(
      `Planning ${tranchePlanEntry.expectedCount} ${trancheLabel.toLowerCase()} resident slots in memory. This can take several minutes.`
    );

    try {
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: requesting ${trancheLabel.toLowerCase()} manifest for ${tranchePlanEntry.expectedCount} permanent residents.`
      );
      const manifest = await requestDraftHouseholdManifest({
        populationTranche: normalizedTranche,
        targetCount: tranchePlanEntry.expectedCount,
        currentSuggestions,
      });
      commitDraftHouseholdManifest(manifest);
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: planned ${manifest.households.length} ${trancheLabel.toLowerCase()} with ${manifest.targetCount} resident slots.`
      );
      finishRun(
        runSummary,
        `${trancheLabel} plan is ready for review: ${manifest.households.length} households and ${manifest.targetCount} permanent residents.`
      );
    } catch (error) {
      const failureMessage = error?.message || 'Failed to plan population households.';
      setErrorMessage(failureMessage);
      setStatusMessage('Household planning stopped.');
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: ${trancheLabel.toLowerCase()} manifest failed: ${failureMessage}`
      );
      setLastRunSummary(runSummary);
    } finally {
      setActiveAction('');
    }
  };

  const requestDraftInhabitantSuggestionBatch = async ({
    currentSuggestions,
    populationTranche,
    requestedCount,
    residentTranchePlan,
    trancheTargetCount,
    householdManifest,
    requestedHouseholdSlots = [],
    replace = false,
  }) => {
    const currentResidentDrafts = selectCurrentResidentDraftsForRequest(
      currentSuggestions,
      {
        replace,
        populationTranche,
      }
    );
    const response = await callCdo(
      cdoPlace.generateInhabitantSuggestions,
      withExplorerPrompt({
        place: {
          ...draftPlace,
          childPlaceSuggestions: draftChildSuggestions,
          inhabitantSuggestions: currentResidentDrafts,
        },
        requestedCount,
        expectedInhabitantCount: normalizeExpectedInhabitantCount(
          draftPlace?.expectedInhabitantCount
        ),
        populationTranche,
        trancheTargetCount,
        residentTranchePlan,
        householdManifest,
        requestedHouseholdSlotIds: requestedHouseholdSlots
          .map((slot) => slot.slotId),
        identityOnly: true,
        aogYear: DEFAULT_AOG_YEAR,
      })
    );

    return normalizeInhabitantSuggestions(response?.inhabitantSuggestions);
  };

  const runDraftInhabitantSuggestionPass = async ({
    replace = false,
    populationTranche = selectedPopulationTranche,
  } = {}) => {
    if (!draftPlace) {
      setErrorMessage('Generate a place draft before generating its population.');
      return;
    }

    const expectedChildPlaceCount = resolveExpectedChildPlaceCount(draftPlace);
    const hasCompletePlan = expectedChildPlaceCount > 0 &&
      draftChildSuggestions.length >= expectedChildPlaceCount;

    if (!hasCompletePlan) {
      setErrorMessage('Complete the planned sub-place suggestions before generating the population.');
      return;
    }

    if (isDraftStructureLocked) {
      setErrorMessage('This population workflow only operates on an unsaved place draft.');
      return;
    }

    const normalizedPopulationTranche = normalizePopulationTranche(populationTranche);
    const residentTranchePlan = normalizeResidentTranchePlan(draftPlace?.residentTranchePlan);
    const plannedPopulation = normalizePlannedPopulation(draftPlace?.plannedPopulation);
    if (!isResidentTranchePlanValid(residentTranchePlan, plannedPopulation)) {
      setErrorMessage(
        'The resident tranche plan must contain all four phases and total the planned population.'
      );
      return;
    }

    const tranchePlanEntry = residentTranchePlan.find(
      ({ tranche }) => tranche === normalizedPopulationTranche
    );
    if (!tranchePlanEntry) {
      setErrorMessage('Select a valid population tranche.');
      return;
    }

    const trancheCounts = getPopulationTrancheCounts(draftInhabitantSuggestions);
    const currentTrancheCount = trancheCounts[normalizedPopulationTranche] ?? 0;
    const trancheLabel = POPULATION_TRANCHES.find(
      ({ key }) => key === normalizedPopulationTranche
    )?.label || normalizedPopulationTranche;
    if (!replace && !canGeneratePopulationTranche(
      normalizedPopulationTranche,
      residentTranchePlan,
      draftInhabitantSuggestions
    )) {
      setErrorMessage(
        currentTrancheCount >= tranchePlanEntry.expectedCount
          ? `${POPULATION_TRANCHES.find(({ key }) => key === normalizedPopulationTranche)?.label || 'This tranche'} is complete.`
          : 'Complete the preceding population tranche first.'
      );
      return;
    }

    if (isHouseholdManifestTranche(normalizedPopulationTranche)) {
      if (replace) {
        const retainedSuggestions = draftInhabitantSuggestions.filter(
          (suggestion) =>
            normalizePopulationTranche(suggestion?.populationTranche) !==
            normalizedPopulationTranche
        );
        commitDraftInhabitantSuggestions(retainedSuggestions);
        clearDraftHouseholdManifestsFrom(normalizedPopulationTranche);
        await runDraftHouseholdManifestPlan({
          populationTranche: normalizedPopulationTranche,
          currentSuggestions: retainedSuggestions,
          replace: true,
        });
        return;
      }

      if (!draftHouseholdManifests[normalizedPopulationTranche]) {
        await runDraftHouseholdManifestPlan({
          populationTranche: normalizedPopulationTranche,
        });
        return;
      }
    } else if (replace) {
      clearDraftHouseholdManifestsFrom(normalizedPopulationTranche);
    }

    setActiveAction('suggest-inhabitants');
    setErrorMessage('');
    setStatusMessage(`Generating ${trancheLabel.toLowerCase()} in memory...`);
    const suggestionCountTowardTarget = replace ? 0 : currentTrancheCount;
    const remainingPlannedInhabitants = Math.max(
      0,
      tranchePlanEntry.expectedCount - suggestionCountTowardTarget
    );
    const householdManifest = draftHouseholdManifests[normalizedPopulationTranche];
    const batchRequest = !replace
      ? getNextPopulationBatchRequest(
          residentTranchePlan,
          draftInhabitantSuggestions,
          {
            populationTranche: normalizedPopulationTranche,
            batchSize: INHABITANT_SUGGESTION_BATCH_SIZE,
            householdManifests: draftHouseholdManifests,
          }
        )
      : null;
    const requestedHouseholdSlots = batchRequest?.requestedHouseholdSlots ?? [];
    const requestedCount = batchRequest?.requestedCount ?? Math.min(
      INHABITANT_SUGGESTION_BATCH_SIZE,
      remainingPlannedInhabitants
    );
    if (requestedCount === 0) {
      setErrorMessage(`${trancheLabel} is complete.`);
      setActiveAction('');
      return;
    }
    const runSummary = createRunSummary(
      replace
        ? `Refresh ${trancheLabel}`
        : `Generate ${trancheLabel}`
    );

    try {
      addLogEntry(
        replace
          ? `${getPlaceLabel(draftPlace)}: requesting replacement ${trancheLabel.toLowerCase()} (${currentTrancheCount}/${tranchePlanEntry.expectedCount} currently drafted).`
          : `${getPlaceLabel(draftPlace)}: requesting ${trancheLabel.toLowerCase()} (${currentTrancheCount}/${tranchePlanEntry.expectedCount} drafted; ${draftInhabitantSuggestions.length}/${plannedPopulation} total population).`
      );
      const returnedSuggestions = await requestDraftInhabitantSuggestionBatch({
        currentSuggestions: draftInhabitantSuggestions,
        populationTranche: normalizedPopulationTranche,
        requestedCount,
        residentTranchePlan,
        trancheTargetCount: tranchePlanEntry.expectedCount,
        householdManifest,
        requestedHouseholdSlots,
        replace,
      });
      const suggestions = mergeInhabitantSuggestionBatches(
        draftInhabitantSuggestions,
        returnedSuggestions,
        {
          replace,
          populationTranche: normalizedPopulationTranche,
        }
      );
      const updatedTrancheCounts = getPopulationTrancheCounts(suggestions);
      const updatedTrancheCount = updatedTrancheCounts[normalizedPopulationTranche] ?? 0;
      if (replace && normalizedPopulationTranche === 'notables') {
        clearDraftNotableLoadouts(suggestions);
      } else {
        commitDraftInhabitantSuggestions(suggestions);
      }
      if (updatedTrancheCount >= tranchePlanEntry.expectedCount) {
        const currentTrancheIndex = POPULATION_TRANCHES.findIndex(
          ({ key }) => key === normalizedPopulationTranche
        );
        const nextTranche = POPULATION_TRANCHES
          .slice(currentTrancheIndex + 1)
          .find(({ key }) =>
            canGeneratePopulationTranche(key, residentTranchePlan, suggestions)
          );
        if (nextTranche) {
          setSelectedPopulationTranche(nextTranche.key);
        }
      }
      addLogEntry(
        `${getPlaceLabel(draftPlace)}: ${returnedSuggestions.length} resident identity draft${returnedSuggestions.length === 1 ? '' : 's'} returned; ${updatedTrancheCount}/${tranchePlanEntry.expectedCount} ${trancheLabel.toLowerCase()} and ${suggestions.length}/${plannedPopulation} permanent residents generated in memory.`
      );
      finishRun(
        runSummary,
        replace
          ? `Replaced ${trancheLabel.toLowerCase()} with ${updatedTrancheCount} resident identity draft${updatedTrancheCount === 1 ? '' : 's'}.`
          : `Added ${returnedSuggestions.length} ${trancheLabel.toLowerCase()}; ${suggestions.length} of ${plannedPopulation} permanent residents are generated in memory.`
      );
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to generate the population batch.');
      setStatusMessage('Population generation stopped.');
      setLastRunSummary(runSummary);
    } finally {
      setActiveAction('');
    }
  };

  const runDraftInhabitantSuggestionSeries = async ({
    populationTranche = '',
  } = {}) => {
    if (!draftPlace) {
      setErrorMessage('Generate a place draft before generating its population.');
      return;
    }

    const expectedChildPlaceCount = resolveExpectedChildPlaceCount(draftPlace);
    const hasCompletePlan = expectedChildPlaceCount > 0 &&
      draftChildSuggestions.length >= expectedChildPlaceCount;
    if (!hasCompletePlan) {
      setErrorMessage('Complete the planned sub-place suggestions before generating the population.');
      return;
    }

    if (isDraftStructureLocked) {
      setErrorMessage('This population workflow only operates on an unsaved place draft.');
      return;
    }

    const residentTranchePlan = normalizeResidentTranchePlan(draftPlace?.residentTranchePlan);
    const plannedPopulation = normalizePlannedPopulation(draftPlace?.plannedPopulation);
    if (!isResidentTranchePlanValid(residentTranchePlan, plannedPopulation)) {
      setErrorMessage(
        'The resident tranche plan must contain all four phases and total the planned population.'
      );
      return;
    }

    const normalizedPopulationTranche = normalizePopulationTranche(populationTranche);
    if (populationTranche && !normalizedPopulationTranche) {
      setErrorMessage('Select a valid population tranche.');
      return;
    }

    if (normalizedPopulationTranche && !canGeneratePopulationTranche(
      normalizedPopulationTranche,
      residentTranchePlan,
      draftInhabitantSuggestions
    )) {
      const tranchePlanEntry = residentTranchePlan.find(
        ({ tranche }) => tranche === normalizedPopulationTranche
      );
      const currentCount = getPopulationTrancheCounts(
        draftInhabitantSuggestions
      )[normalizedPopulationTranche] ?? 0;
      setErrorMessage(
        currentCount >= (tranchePlanEntry?.expectedCount ?? 0)
          ? `${POPULATION_TRANCHES.find(({ key }) => key === normalizedPopulationTranche)?.label || 'This tranche'} is complete.`
          : 'Complete the preceding population tranche first.'
      );
      return;
    }

    const initialRequest = getNextPopulationBatchRequest(
      residentTranchePlan,
      draftInhabitantSuggestions,
      {
        populationTranche: normalizedPopulationTranche,
        batchSize: INHABITANT_SUGGESTION_BATCH_SIZE,
        householdManifests: draftHouseholdManifests,
      }
    );
    if (!initialRequest) {
      setErrorMessage('The planned permanent population is complete.');
      return;
    }

    const scopedTrancheLabel = normalizedPopulationTranche
      ? POPULATION_TRANCHES.find(({ key }) => key === normalizedPopulationTranche)?.label
      : '';
    const activeSeriesAction = normalizedPopulationTranche
      ? 'complete-inhabitant-tranche'
      : 'suggest-all-inhabitants';
    const runSummary = createRunSummary(
      normalizedPopulationTranche
        ? `Complete ${scopedTrancheLabel}`
        : 'Generate All Remaining Population'
    );
    let latestSuggestions = draftInhabitantSuggestions;
    let completedBatchCount = 0;

    populationGenerationStopRequestedRef.current = false;
    setIsPopulationGenerationStopRequested(false);
    setActiveAction(activeSeriesAction);
    setErrorMessage('');
    setStatusMessage(
      normalizedPopulationTranche
        ? `Completing ${scopedTrancheLabel.toLowerCase()} in serial batches...`
        : 'Generating all remaining permanent residents in serial batches...'
    );

    try {
      const result = await generatePopulationBatchesSerially({
        plan: residentTranchePlan,
        suggestions: draftInhabitantSuggestions,
        populationTranche: normalizedPopulationTranche,
        batchSize: INHABITANT_SUGGESTION_BATCH_SIZE,
        householdManifests: draftHouseholdManifests,
        shouldContinue: () => !populationGenerationStopRequestedRef.current,
        requestHouseholdManifest: async ({
          populationTranche: manifestTranche,
          targetCount,
          currentSuggestions,
        }) => {
          const trancheLabel = POPULATION_TRANCHES.find(
            ({ key }) => key === manifestTranche
          )?.label || manifestTranche;
          setSelectedPopulationTranche(manifestTranche);
          setStatusMessage(`Planning ${trancheLabel.toLowerCase()} in memory...`);
          addLogEntry(
            `${getPlaceLabel(draftPlace)}: requesting ${trancheLabel.toLowerCase()} manifest for ${targetCount} permanent residents.`
          );
          return requestDraftHouseholdManifest({
            populationTranche: manifestTranche,
            targetCount,
            currentSuggestions,
          });
        },
        onHouseholdManifest: ({
          populationTranche: manifestTranche,
          manifest,
        }) => {
          const trancheLabel = POPULATION_TRANCHES.find(
            ({ key }) => key === manifestTranche
          )?.label || manifestTranche;
          commitDraftHouseholdManifest(manifest);
          addLogEntry(
            `${getPlaceLabel(draftPlace)}: planned ${manifest.households.length} ${trancheLabel.toLowerCase()} with ${manifest.targetCount} resident slots; generation paused for review.`
          );
        },
        requestBatch: async ({
          populationTranche: requestTranche,
          requestedCount,
          currentCount,
          targetCount,
          currentSuggestions,
          batchNumber,
          requestedHouseholdSlots,
        }) => {
          const trancheLabel = POPULATION_TRANCHES.find(
            ({ key }) => key === requestTranche
          )?.label || requestTranche;
          setSelectedPopulationTranche(requestTranche);
          setStatusMessage(
            `Generating ${trancheLabel.toLowerCase()} batch ${batchNumber} (${currentCount}/${targetCount})...`
          );
          addLogEntry(
            `${getPlaceLabel(draftPlace)}: requesting serial ${trancheLabel.toLowerCase()} batch ${batchNumber} (${currentCount}/${targetCount} drafted; ${currentSuggestions.length}/${plannedPopulation} total population).`
          );
          return requestDraftInhabitantSuggestionBatch({
            currentSuggestions,
            populationTranche: requestTranche,
            requestedCount,
            residentTranchePlan,
            trancheTargetCount: targetCount,
            householdManifest: draftHouseholdManifests[requestTranche],
            requestedHouseholdSlots,
          });
        },
        onBatch: ({
          populationTranche: completedTranche,
          returnedSuggestions,
          suggestions,
          updatedTrancheCount,
          targetCount,
          batchNumber,
        }) => {
          const trancheLabel = POPULATION_TRANCHES.find(
            ({ key }) => key === completedTranche
          )?.label || completedTranche;
          latestSuggestions = suggestions;
          completedBatchCount = batchNumber;
          commitDraftInhabitantSuggestions(suggestions);
          addLogEntry(
            `${getPlaceLabel(draftPlace)}: serial batch ${batchNumber} returned ${returnedSuggestions.length} ${trancheLabel.toLowerCase()}; ${updatedTrancheCount}/${targetCount} in tranche and ${suggestions.length}/${plannedPopulation} total population.`
          );
        },
      });

      latestSuggestions = result.suggestions;
      completedBatchCount = result.batchCount;
      const nextRequest = getNextPopulationBatchRequest(
        residentTranchePlan,
        result.suggestions,
        {
          batchSize: INHABITANT_SUGGESTION_BATCH_SIZE,
          householdManifests: result.householdManifests,
        }
      );
      if (nextRequest) {
        setSelectedPopulationTranche(nextRequest.populationTranche);
      }

      finishRun(
        runSummary,
        result.pausedForManifest
          ? `${POPULATION_TRANCHES.find(({ key }) => key === result.plannedManifestTranche)?.label || 'Household'} plan is ready for review. Population generation paused after ${result.batchCount} completed serial batch${result.batchCount === 1 ? '' : 'es'}.`
          : result.stopped
          ? `Stopped after ${result.batchCount} serial batch${result.batchCount === 1 ? '' : 'es'}; ${result.suggestions.length} of ${plannedPopulation} permanent residents are generated in memory.`
          : normalizedPopulationTranche
            ? `Completed ${scopedTrancheLabel.toLowerCase()} in ${result.batchCount} serial batch${result.batchCount === 1 ? '' : 'es'}; ${result.suggestions.length} of ${plannedPopulation} permanent residents are generated in memory.`
            : `Generated all ${result.suggestions.length} permanent residents in ${result.batchCount} serial batch${result.batchCount === 1 ? '' : 'es'}.`
      );
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to generate the remaining population.');
      setStatusMessage(
        `Population generation stopped after ${completedBatchCount} completed serial batch${completedBatchCount === 1 ? '' : 'es'}; ${latestSuggestions.length} of ${plannedPopulation} residents remain in memory.`
      );
      setLastRunSummary(runSummary);
    } finally {
      populationGenerationStopRequestedRef.current = false;
      setIsPopulationGenerationStopRequested(false);
      setActiveAction('');
    }
  };

  const stopPopulationGenerationAfterCurrentBatch = () => {
    populationGenerationStopRequestedRef.current = true;
    setIsPopulationGenerationStopRequested(true);
    setStatusMessage('Stopping population generation after the current batch returns...');
  };

  const worldDraft = {
    draftPlace: draftPlace ? { ...draftPlace, savedPlaceId: savedDraftPlaceId } : null,
    draftChildSuggestions: sortIndexedChildPlaceSuggestionsForPlacement(
      draftChildSuggestions.map((suggestion, index) => ({ suggestion, index }))
    ).map(({ suggestion }) => suggestion),
    draftInhabitantSuggestions, draftCommercialInventoryItems, draftNotableLoadoutItems,
    draftEvents, selectedParentPlaceId,
    worldDevelopment, worldSaveProgress,
    sourcePrompt: sourcePromptRef.current, prompt,
  };

  const generateEvents = async () => {
    if (isBusy || !draftPlace) return;
    setActiveAction('generate-events');
    setErrorMessage('');
    setStatusMessage('Generating events from the current Places, Beings, and Items...');
    try {
      const plannedDevelopment = await planWorldSocialLives({ draft: worldDraft, development: worldDevelopment,
        api: cdoGeneratedWorld, onProgress: setWorldDevelopment, onStatus: setStatusMessage });
      const plannedDraft = { ...worldDraft, worldDevelopment: plannedDevelopment };
      const catalog = getAutomationCatalog(plannedDraft).map(entry => ({ ...entry }));
      const result = await cdoGeneratedWorld.generateEvents({
        existingRelationships: getExistingWorldRelationships(plannedDraft),
        catalog, requestedCount: eventCount, currentDate: { age: 'The Third Age', year: 1000, season: 'Oreth', day: 1 },
        prompt: sourcePromptRef.current || prompt, existingEvents: draftEvents,
      });
      const nextEvents = (result.events || []).map(event => ({
        ...event, draftEventId: createWorldDraftId('draft-event'),
      }));
      setDraftEvents(current => [...current, ...nextEvents]);
      setStatusMessage(`Generated ${nextEvents.length} event drafts for review. They will be saved with the Place Tree.`);
    } catch (error) {
      setErrorMessage(error.message || 'Event generation failed.');
      setStatusMessage('Event generation stopped. Existing event drafts are kept.');
    } finally {
      setActiveAction('');
    }
  };

  const createCompletePlaceTree = async () => {
    if (isBusy) return;
    try { validateAutomationCounts(automation); }
    catch (error) { setErrorMessage(error.message); return; }
    setActiveAction('automatic-place-tree');
    setErrorMessage('');
    automaticStopRef.current = false;
    setIsAutomaticStopRequested(false);
    const publish = next => {
      setAutomation(next.automation);
      sourcePromptRef.current = next.sourcePrompt || next.prompt;
      setDraftPlace(next.draftPlace);
      setDraftChildSuggestions(next.draftChildSuggestions || []);
      setDraftInhabitantSuggestions(next.draftInhabitantSuggestions || []);
      setDraftHouseholdManifests(next.draftHouseholdManifests || {});
      setDraftNotableLoadoutManifest(next.draftNotableLoadoutManifest || null);
      setDraftNotableLoadoutItems(next.draftNotableLoadoutItems || []);
      setDraftNotableCurrencyHoldings(next.draftNotableCurrencyHoldings || []);
      setDraftCommercialInventoryManifest(next.draftCommercialInventoryManifest || null);
      setDraftCommercialInventoryItems(next.draftCommercialInventoryItems || []);
      setDraftEstablishmentCurrencyHoldings(next.draftEstablishmentCurrencyHoldings || []);
      setDraftEvents(next.draftEvents || []);
      setWorldDevelopment(next.worldDevelopment);
      setWorldSaveProgress(next.worldSaveProgress);
      latestSessionDraftRef.current = next;
    };
    try {
      const result = await createPlaceTreeAutomatically({ draft: currentSessionDraft, api: cdoGeneratedWorld,
        requestPlace: (method, data) => callCdo(cdoPlace[method], data), onProgress: publish, onStatus: setStatusMessage,
        shouldStop: () => automaticStopRef.current,
        checkpoint: async next => {
          const backup = savePlaceTreeSessionDraft(next);
          const result = backup.pending ? await backup.pending : backup;
          if (!result.ok) throw new Error(`Automatic creation paused because its draft backup failed: ${result.error?.message || 'storage unavailable'}`);
        },
        normalizers: { children: sortChildPlaceSuggestionsForPlacement, residents: normalizeInhabitantSuggestions,
          mergeChildren: mergeChildPlaceSuggestions, expectedChildCount: resolveExpectedChildPlaceCount } });
      if (result.stopped) {
        setStatusMessage('Automatic creation stopped after the current request. Completed drafts are kept; resume to continue.');
        return;
      }
      const savingDraft = { ...result.draft, automation: { ...result.draft.automation, stage: 'Saving' } };
      publish(savingDraft);
      const saved = await savePlaceTree(savingDraft, { automatic: true });
      if (saved && !saved.stopped) {
        const complete = { ...savingDraft.automation, stage: 'Complete', complete: true };
        setAutomation(complete);
        latestSessionDraftRef.current = { ...latestSessionDraftRef.current, graph: graphRef.current,
          worldSaveProgress: saved.progress, automation: complete };
        const backup = savePlaceTreeSessionDraft(latestSessionDraftRef.current);
        monitorPlaceTreeBackup(backup, setErrorMessage, persistenceErrorRef);
      }
    } catch (error) {
      setErrorMessage(error.message || 'Automatic Place Tree creation failed.');
      setStatusMessage('Automatic creation stopped. Completed drafts are kept; run it again to resume.');
    } finally { setActiveAction(''); setIsAutomaticStopRequested(false); }
  };

  const savePlaceTree = async (sourceDraft = worldDraft, { automatic = false } = {}) => {
    if ((!automatic && isBusy) || !sourceDraft.draftPlace) return;
    const expectedChildPlaceCount = resolveExpectedChildPlaceCount(sourceDraft.draftPlace);
    if (expectedChildPlaceCount > sourceDraft.draftChildSuggestions.length) {
      setErrorMessage('Complete the planned sub-place suggestions before saving.');
      return;
    }
    if (getRecordPreparationTasks(sourceDraft).length) {
      setErrorMessage('Prepare the remaining final records before saving. Review Events, then use Prepare All Records.');
      return;
    }
    setActiveAction('save-place-tree');
    setErrorMessage('');
    const runSummary = createRunSummary('Save Place Tree');
    try {
      await new Promise(resolve => setTimeout(resolve, 0));
      const initialBackup = savePlaceTreeSessionDraft(latestSessionDraftRef.current);
      const confirmedBackup = initialBackup.pending ? await initialBackup.pending : initialBackup;
      if (!confirmedBackup.ok) throw new Error(`Could not back up the Place Tree before saving: ${confirmedBackup.error?.message || 'storage unavailable'}`);
      const result = await savePlaceTreeWorld({
        draft: sourceDraft, progress: sourceDraft.worldSaveProgress,
        api: automatic ? Object.fromEntries(['savePlace', 'saveEntity', 'saveCurrencies', 'saveRelationships', 'generateRoutines', 'planKnowledge', 'storyStatus', 'storyStep', 'installStory'].map(method =>
          [method, data => retryPlaceTreeRequest(() => cdoGeneratedWorld[method](data), { shouldStop: () => automaticStopRef.current,
            onRetry: attempt => setStatusMessage(`Temporary save failure; retry ${attempt} of 2...`) })])) : cdoGeneratedWorld,
        shouldStop: () => automatic && automaticStopRef.current,
        onStatus: setStatusMessage,
        onRoutineSaved: (entry, record, ids) => {
          commitGraph(mergeBeing(graphRef.current, { ...entry.entity, ...record }, { placeId: ids[entry.entity.memberOfPlaceId] }));
        },
        onProgress: next => {
          setIsDraftStructureLocked(true);
          setWorldSaveProgress(next);
          latestSessionDraftRef.current = {
            ...latestSessionDraftRef.current, worldSaveProgress: next, isDraftStructureLocked: true,
          };
          const backup = savePlaceTreeSaveProgress(next);
          monitorPlaceTreeBackup(backup, setErrorMessage, persistenceErrorRef);
          if (!backup.ok) addLogEntry('Save progress could not be backed up in this tab. Keep the tab open to retry.');
          if (automatic && !backup.ok) throw new Error(`Automatic save paused because its progress checkpoint failed: ${backup.error?.message || 'storage unavailable'}`);
        },
        onSaved: (entry, record, ids) => {
          const collection = entry.kind === 'Place' ? graphRef.current.placesById
            : entry.kind === 'Being' ? graphRef.current.beingsById : graphRef.current.itemsById;
          if (record.reused && (entry.kind === 'Event' || collection[record.idEntity])) return;
          const saved = { ...entry.entity, ...record };
          if (entry.kind === 'Place') {
            const rootId = sourceDraft.draftPlace.draftPlaceId || 'draft-place:root';
            const isRoot = entry.draftId === rootId;
            commitGraph(ingestPlace(graphRef.current, saved, {
              setAsRoot: isRoot, parentPlaceId: record.parentIdEntity,
            }));
            if (isRoot) {
              setSavedDraftPlaceId(record.idEntity);
              setSelectedNodeKey(`place:${record.idEntity}`);
            } else {
              if (entry.entity.isNearby) setWorldDevelopment(current => ({ ...current,
                nearbyPlaces: current.nearbyPlaces.map(place => place.draftPlaceId === entry.draftId
                  ? { ...place, savedPlaceId: record.idEntity, savedPlaceName: saved.name } : place)
              }));
              setDraftChildSuggestions(current => current.map(suggestion =>
                suggestion.draftPlaceId === entry.draftId
                  ? { ...suggestion, savedPlaceId: record.idEntity, savedPlaceName: saved.name }
                  : suggestion));
            }
            if (!record.reused) runSummary.placesCreated += 1;
          } else if (entry.kind === 'Being') {
            commitGraph(mergeBeing(graphRef.current, saved, { placeId: ids[entry.entity.memberOfPlaceId] }));
          } else if (entry.kind === 'Item') {
            const ownerId = ids[entry.entity.ownerDraftBeingId || entry.entity.commercialPlaceId];
            commitGraph(mergeItem(graphRef.current, saved, {
              ownerId, ownerType: entry.entity.ownerDraftBeingId ? 'being' : 'place',
            }));
          }
        },
      });
      if (result.stopped) {
        setStatusMessage('Save paused after the current request. Saved records are kept; resume to continue.');
        return result;
      }
      const { Place, Being, Item, Event } = result.counts;
      Object.assign(runSummary, { beingsSaved: Being, itemsSaved: Item, eventsSaved: Event });
      finishRun(runSummary, `Saved ${Place} Places, ${Being} Beings, ${Item} Items, and ${Event} Events, including their relationships, generated currencies, and daily routines.`);
      return result;
    } catch (error) {
      recordFailure(runSummary, 'Save Place Tree', error);
      setErrorMessage(error.message || 'Failed to save the Place Tree.');
      setStatusMessage('Save stopped. Saved records are kept; Retry Place Tree Save will continue from that point.');
      setLastRunSummary(runSummary);
    } finally {
      if (!automatic) setActiveAction('');
    }
  };

  const buildPlaceNode = (placeId) => {
    const place = graph.placesById[placeId];
    if (!place) {
      return null;
    }

    const childPlaceNodes = (Array.isArray(place.childPlaceIds) ? place.childPlaceIds : [])
      .map((childPlaceId) => buildPlaceNode(childPlaceId))
      .filter(Boolean);

    const inhabitantNodes = (Array.isArray(place.inhabitantIds) ? place.inhabitantIds : [])
      .map((beingId) => buildBeingNode(beingId))
      .filter(Boolean);

    const itemNodes = (Array.isArray(place.itemIds) ? place.itemIds : [])
      .map((itemId) => buildItemNode(itemId))
      .filter(Boolean);

    return {
      key: `place:${place.idEntity}`,
      type: 'place',
      idEntity: place.idEntity,
      name: getPlaceLabel(place),
      title: place.title,
      placeType: place.placeType,
      zoneType: place.zoneType,
      buildingType: place.buildingType,
      children: [...childPlaceNodes, ...inhabitantNodes, ...itemNodes],
    };
  };

  const buildBeingNode = (beingId) => {
    const being = graph.beingsById[beingId];
    if (!being) {
      return null;
    }

    const itemNodes = (Array.isArray(being.itemIds) ? being.itemIds : [])
      .map((itemId) => buildItemNode(itemId))
      .filter(Boolean);

    return {
      key: `being:${being.idEntity}`,
      type: 'being',
      idEntity: being.idEntity,
      name: getBeingLabel(being),
      title: being.title,
      level: being.level,
      beingType: being.beingType,
      children: itemNodes,
    };
  };

  const buildItemNode = (itemId) => {
    const item = graph.itemsById[itemId];
    if (!item) {
      return null;
    }

    return {
      key: `item:${item.idEntity}`,
      type: 'item',
      idEntity: item.idEntity,
      name: getItemLabel(item),
      title: item.title,
      level: item.level,
      itemType: item.itemType,
      children: [],
    };
  };

  const handleGenerateTown = async (event) => {
    event.preventDefault();
    await runInitialTownGeneration();
  };

  const rootNode = automation.mode === 'step-by-step' && graph.rootPlaceId ? buildPlaceNode(graph.rootPlaceId) : null;
  const plannedPopulation = draftPlace ? normalizePlannedPopulation(draftPlace.plannedPopulation) : 0;
  const plannedSubPlaceCount = draftPlace ? resolveExpectedChildPlaceCount(draftPlace) : 0;
  const plannedInhabitantCount = draftPlace
    ? normalizeExpectedInhabitantCount(draftPlace.expectedInhabitantCount)
    : 0;
  const residentTranchePlan = normalizeResidentTranchePlan(draftPlace?.residentTranchePlan);
  const residentTranchePlanTotal = getResidentTranchePlanTotal(residentTranchePlan);
  const isPopulationPlanValid = isResidentTranchePlanValid(
    residentTranchePlan,
    plannedPopulation
  );
  const populationTrancheCounts = getPopulationTrancheCounts(draftInhabitantSuggestions);
  const selectedPopulationTrancheLabel = POPULATION_TRANCHES.find(
    ({ key }) => key === selectedPopulationTranche
  )?.label || 'Population';
  const draftedSubPlaceCount = draftChildSuggestions.length;
  const draftedInhabitantCount = draftInhabitantSuggestions.length;
  const isSubPlacePlanComplete = plannedSubPlaceCount > 0 && draftedSubPlaceCount >= plannedSubPlaceCount;
  const canSuggestInhabitants = Boolean(draftPlace) &&
    isSubPlacePlanComplete &&
    !isDraftStructureLocked;
  const isPlaceTreeSaved = isWorldSaveComplete(worldDraft, worldSaveProgress);
  const canSavePlaceTree = Boolean(draftPlace) &&
    (plannedSubPlaceCount === 0 || isSubPlacePlanComplete) &&
    getRecordPreparationTasks(worldDraft).length === 0 &&
    !isPlaceTreeSaved;
  const selectedTrancheIndex = POPULATION_TRANCHES.findIndex(
    ({ key }) => key === selectedPopulationTranche
  );
  const hasGeneratedLaterTranche = POPULATION_TRANCHES
    .slice(selectedTrancheIndex + 1)
    .some(({ key }) => (populationTrancheCounts[key] ?? 0) > 0);
  const canAdvanceSelectedPopulationTranche = canSuggestInhabitants &&
    isPopulationPlanValid &&
    canGeneratePopulationTranche(
      selectedPopulationTranche,
      residentTranchePlan,
      draftInhabitantSuggestions
    );
  const selectedHouseholdManifest = normalizeHouseholdManifest(
    draftHouseholdManifests[selectedPopulationTranche]
  );
  const selectedHouseholdManifestProgress = selectedHouseholdManifest
    ? getHouseholdManifestProgress(
        selectedHouseholdManifest,
        draftInhabitantSuggestions
      )
    : null;
  const selectedTrancheNeedsHouseholdManifest =
    isHouseholdManifestTranche(selectedPopulationTranche) &&
    !selectedHouseholdManifest;
  const canPlanSelectedHouseholds =
    canAdvanceSelectedPopulationTranche &&
    selectedTrancheNeedsHouseholdManifest;
  const canGenerateSelectedPopulationTranche =
    canAdvanceSelectedPopulationTranche &&
    !selectedTrancheNeedsHouseholdManifest;
  const canRegenerateSelectedHouseholdManifest =
    canAdvanceSelectedPopulationTranche &&
    Boolean(selectedHouseholdManifest) &&
    (populationTrancheCounts[selectedPopulationTranche] ?? 0) === 0 &&
    !hasGeneratedLaterTranche;
  const canRefreshSelectedPopulationTranche = canSuggestInhabitants &&
    isPopulationPlanValid &&
    (populationTrancheCounts[selectedPopulationTranche] ?? 0) > 0 &&
    !hasGeneratedLaterTranche;
  const nextRemainingPopulationBatch = isPopulationPlanValid
    ? getNextPopulationBatchRequest(
        residentTranchePlan,
        draftInhabitantSuggestions,
        {
          batchSize: INHABITANT_SUGGESTION_BATCH_SIZE,
          householdManifests: draftHouseholdManifests,
        }
      )
    : null;
  const canGenerateAllRemainingPopulation = canSuggestInhabitants &&
    Boolean(nextRemainingPopulationBatch);
  const isSerialPopulationGeneration = [
    'complete-inhabitant-tranche',
    'suggest-all-inhabitants',
  ].includes(activeAction);
  const subPlaceProgressText = plannedSubPlaceCount > 0
    ? `${draftedSubPlaceCount} of ${plannedSubPlaceCount} suggested`
    : `${draftedSubPlaceCount} suggested`;
  const populationProgressText = plannedPopulation > 0
    ? `${draftedInhabitantCount} of ${plannedPopulation} generated`
    : `${draftedInhabitantCount} generated`;
  const childPlacePlan = normalizeChildPlacePlan(draftPlace?.childPlacePlan);
  const childPlacePlanSummary = childPlacePlan
    .map((entry) => `${entry.category || 'Uncategorized'} (${entry.expectedCount})`)
    .join(', ');
  const notableTargetCount = residentTranchePlan.find(
    ({ tranche }) => tranche === 'notables'
  )?.expectedCount ?? 0;
  const notableDraftCount = populationTrancheCounts.notables ?? 0;
  const notableLoadoutProgress = draftNotableLoadoutManifest
    ? getNotableLoadoutProgress(
        draftNotableLoadoutManifest,
        draftNotableLoadoutItems
      )
    : null;
  const nextNotableLoadoutBatch = draftNotableLoadoutManifest
    ? getNextNotableLoadoutBatch(
        draftNotableLoadoutManifest,
        draftNotableLoadoutItems,
        NOTABLE_LOADOUT_BATCH_SIZE
      )
    : null;
  const canPlanNotableLoadouts = Boolean(draftPlace) &&
    isSubPlacePlanComplete &&
    !isDraftStructureLocked &&
    notableTargetCount > 0 &&
    notableDraftCount === notableTargetCount &&
    !draftNotableLoadoutManifest;
  const canRegenerateNotableLoadoutPlan = Boolean(
    draftNotableLoadoutManifest
  ) &&
    draftNotableLoadoutItems.length === 0 &&
    !isDraftStructureLocked;
  const canGenerateNotableLoadouts = Boolean(
    draftNotableLoadoutManifest && nextNotableLoadoutBatch
  ) && !isDraftStructureLocked;
  const isSerialNotableLoadoutGeneration =
    activeAction === 'generate-notable-loadouts';
  const inventoryDraftPlaces = getInventoryDraftPlaces(
    draftPlace,
    draftChildSuggestions
  );
  const commercialInventoryProgress = draftCommercialInventoryManifest
    ? getCommercialInventoryProgress(
        draftCommercialInventoryManifest,
        draftCommercialInventoryItems
      )
    : null;
  const nextCommercialInventoryBatch = draftCommercialInventoryManifest
    ? getNextCommercialInventoryBatch(
        draftCommercialInventoryManifest,
        draftCommercialInventoryItems,
        COMMERCIAL_INVENTORY_BATCH_SIZE
      )
    : null;
  const canPlanCommercialInventory = Boolean(draftPlace) &&
    isSubPlacePlanComplete &&
    !isDraftStructureLocked &&
    inventoryDraftPlaces.length > 0 &&
    !draftCommercialInventoryManifest;
  const canRegenerateCommercialInventoryPlan = Boolean(
    draftCommercialInventoryManifest
  ) &&
    draftCommercialInventoryItems.length === 0 &&
    !isDraftStructureLocked;
  const canGenerateCommercialInventory = Boolean(
    draftCommercialInventoryManifest &&
    nextCommercialInventoryBatch
  ) && !isDraftStructureLocked;
  const isSerialInventoryGeneration =
    activeAction === 'generate-commercial-inventory';
  const draftSummaryCounts = {
    places: draftPlace ? 1 + draftChildSuggestions.length : 0,
    residents: draftInhabitantSuggestions.length,
    notableItems: draftNotableLoadoutItems.length,
    tradeItems: draftCommercialInventoryItems.length,
    currencyHolders:
      draftNotableCurrencyHoldings.length +
      draftEstablishmentCurrencyHoldings.length,
  };

  return (
    <div className="explore-panel">
      <PlaceTreeStoryPanel placeOptions={placeOptions} savedRootPlaceId={savedDraftPlaceId}
        savedRootName={draftPlace?.name} disabled={isBusy}
        onBusyChange={busy => setActiveAction(busy ? 'complete-place' : '')} />

      <section className="generator-panel">
        <button type="button" disabled={isBusy || isFindingBackups} onClick={async () => {
          setIsFindingBackups(true);
          try { setRecoverableBackups(await listPlaceTreeBackups()); }
          catch (error) { setErrorMessage(`Could not search stored drafts: ${error.message}`); }
          finally { setIsFindingBackups(false); }
        }}>{isFindingBackups ? 'Finding stored drafts…' : 'Find Stored Place Tree Drafts'}</button>
        <button type="button" className="danger-button" disabled={isBusy || isFindingBackups}
          onClick={purgeStoredDrafts}
          title="Delete browser draft backups and clear the loaded draft. Saved database records are kept.">
          {activeAction === 'purge-place-tree' ? 'Purging stored drafts...' : 'Purge Memory of Stored Place Tree Drafts'}
        </button>
        {recoverableBackups && <div>
          {recoverableBackups.length ? <p>Stored drafts can be recovered after closing a tab. Choose the draft to restore.</p>
            : <p>No stored Place Tree drafts were found in this browser.</p>}
          {recoverableBackups.map(backup => <div key={backup.id}>
            <strong>{backup.name}</strong> · {backup.places} Places · {backup.beings} Beings · {backup.items} Item drafts · {backup.events} Events · {backup.savedRecords} saved records
            {backup.savedAt && <span> · {new Date(backup.savedAt).toLocaleString()}</span>}
            <button type="button" disabled={isBusy} onClick={() => {
              if (draftPlace && !window.confirm('Restore this stored draft in the editor? The current draft backup will be retained.')) return;
              setSessionInitialization(current => ({ ...current, restoring: true, restorationError: '', recoverBackupId: backup.id }));
            }}>Restore {backup.name}</button>
          </div>)}
        </div>}
      </section>
      <fieldset disabled={isBusy}>
        <legend>Creation mode</legend>
        <label><input type="radio" name="place-tree-mode" checked={automation.mode === 'automatic'}
          onChange={() => setAutomation(current => ({ ...current, mode: 'automatic' }))} /> Automatic</label>
        <label><input type="radio" name="place-tree-mode" checked={automation.mode === 'step-by-step'}
          onChange={() => setAutomation(current => ({ ...current, mode: 'step-by-step' }))} /> Step-by-Step</label>
      </fieldset>
      <form className="explore-form" onSubmit={event => {
        event.preventDefault();
        if (automation.mode === 'automatic') createCompletePlaceTree();
        else handleGenerateTown(event);
      }}>
        <label className="editor-field" htmlFor="explore-parent-place">
          Parent place
          <select
            id="explore-parent-place"
            className="select-input"
            value={selectedParentPlaceId}
            onChange={(event) => setSelectedParentPlaceId(event.target.value)}
            disabled={isBusy || (automation.mode === 'automatic' && Boolean(draftPlace))}
          >
            <option value="">No parent selected</option>
            {placeOptions.map((option) => (
              <option key={`explore-parent-${option.value}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="editor-field" htmlFor="explore-prompt">
          Place prompt
          <textarea
            id="explore-prompt"
            className="text-input editor-textarea explore-prompt"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Describe the place, site, farm, district, compound, or settlement to generate..."
            disabled={isBusy || (automation.mode === 'automatic' && Boolean(draftPlace))}
          />
        </label>
        {automation.mode === 'automatic' && <div className="explore-detail-grid">
          <label className="editor-field">Nearby adventure place count
            <input type="number" min="0" max="200" step="1" className="text-input" value={automation.nearbyCount}
              disabled={isBusy || isPlaceTreeSaved || Object.keys(worldSaveProgress.savedIds).length > 0}
              onChange={event => setAutomation(current => ({ ...current, nearbyCount: event.target.value, complete: false }))} />
          </label>
          <label className="editor-field">Event count
            <input type="number" min="0" max="200" step="1" className="text-input" value={automation.eventCount}
              disabled={isBusy || isPlaceTreeSaved || Object.keys(worldSaveProgress.savedIds).length > 0}
              onChange={event => setAutomation(current => ({ ...current, eventCount: event.target.value, complete: false }))} />
          </label>
          <p>Totals for this tree, generated in batches of up to 8. Existing drafts count toward each total.</p>
        </div>}
        {automation.mode === 'automatic' && <div className="form-actions explore-actions">
          <button type="submit" disabled={isBusy || isPlaceTreeSaved || (!draftPlace && !prompt.trim())}>
            {isPlaceTreeSaved ? 'Complete' : isBusy ? 'Generating and Saving...'
              : automation.started || draftPlace ? 'Resume Complete Place Tree' : 'Generate and Save Complete Place Tree'}
          </button>
          {isBusy && <button type="button" disabled={isAutomaticStopRequested}
            onClick={() => { automaticStopRef.current = true; setIsAutomaticStopRequested(true); }}>Stop After Current Request</button>}
        </div>}
        {automation.mode === 'step-by-step' && <div className="form-actions explore-actions">
          <button type="submit" className="button-with-icon" disabled={isBusy}>
            {activeAction === 'generate-town' ? 'Generating...' : 'Generate Place Draft'}
          </button>
          <button
            type="button"
            className="button-with-icon"
            disabled={isBusy || !draftPlace || isSubPlacePlanComplete || isDraftStructureLocked}
            onClick={() => runDraftSubPlaceSuggestionPass()}
          >
            {activeAction === 'suggest-subplaces'
              ? 'Suggesting...'
              : draftedSubPlaceCount > 0
                ? 'Suggest More Sub-Places'
                : 'Suggest Sub-Places'}
          </button>
          <button
            type="button"
            className="button-with-icon"
            disabled={isBusy || !canSavePlaceTree}
            onClick={() => savePlaceTree()}
            title="Prepare final records first, then save Places, Beings, Items, Events, relationships, and currencies, and generate daily routines for every inhabitant."
          >
            {activeAction === 'save-place-tree'
              ? 'Saving Place Tree...'
              : isPlaceTreeSaved
                ? 'Place Tree Saved'
                : savedDraftPlaceId || Object.keys(worldSaveProgress.savedIds).length > 0
                  ? 'Retry Place Tree Save'
                  : 'Save Place Tree'}
          </button>
        </div>}
        <div className="form-actions explore-actions">
          <button
            type="button"
            className="button-with-icon danger-button"
            disabled={
              isBusy || !hasPlaceTreeSessionContent(currentSessionDraft)
            }
            onClick={clearSessionDraft}
            title="Clear the in-tab Place Tree draft; already-saved database Places are not deleted"
          >
            Clear Place Tree Draft
          </button>
        </div>
      </form>

      {statusMessage && <div className="panel-status">{statusMessage}</div>}
      {errorMessage && <div className="panel-status error-status">{errorMessage}</div>}

      {automation.mode === 'automatic' && draftPlace && <section className="editor-section">
        <h3>{getPlaceLabel(draftPlace)}</h3>
        <p>Stage: {isPlaceTreeSaved ? 'Complete' : automation.stage || 'Ready'}</p>
        <p>{draftSummaryCounts.places + (worldDevelopment.nearbyPlaces || []).length} Places, {draftSummaryCounts.residents + (worldDevelopment.nearbyInhabitants || []).length} Beings, {draftSummaryCounts.notableItems + draftSummaryCounts.tradeItems} Item drafts, {draftEvents.length} Events</p>
        <p>{Object.keys(worldSaveProgress.savedIds).length} saved records. Switch to Step-by-Step to inspect or edit the drafts.</p>
      </section>}
      {automation.mode === 'step-by-step' && <>
      {draftPlace && (
        <section className="editor-section">
          <div className="section-header">
            <h3>{savedDraftPlaceId ? 'Saved Place' : 'Place Draft'}</h3>
            {savedDraftPlaceId && <span className="existing-list-title">idEntity: {savedDraftPlaceId}</span>}
          </div>
          <div className="explore-detail-grid town-draft-detail-grid">
            <div className="empty-grid">
              <strong>Name</strong>
              <div>{getPlaceLabel(draftPlace)}</div>
            </div>
            <div className="empty-grid">
              <strong>Title</strong>
              <div>{draftPlace.title || draftPlace.entityTitle || ''}</div>
            </div>
            <div className="empty-grid">
              <strong>Type</strong>
              <div>{[draftPlace.placeType, draftPlace.zoneType, draftPlace.buildingType].filter(Boolean).join(' / ')}</div>
            </div>
            <div className="empty-grid">
              <strong>Level</strong>
              <div>{draftPlace.level ?? ''}</div>
            </div>
            <div className="empty-grid">
              <strong>Planned Population</strong>
              <div>{formatPlannedPopulation(plannedPopulation)}</div>
            </div>
            <div className="empty-grid">
              <strong>Center</strong>
              <div>
                {draftPlace.location
                  ? [draftPlace.location.x, draftPlace.location.y, draftPlace.location.z].join(', ')
                  : ''}
              </div>
            </div>
            <div className="empty-grid">
              <strong>Radius</strong>
              <div>{draftPlace.location?.maxRadius ?? ''}</div>
            </div>
            <div className="empty-grid">
              <strong>Planned Sub-Places</strong>
              <div>{plannedSubPlaceCount > 0 ? plannedSubPlaceCount : 'None expected'}</div>
            </div>
            <div className="empty-grid">
              <strong>Planned Notable Inhabitants</strong>
              <div>{plannedInhabitantCount > 0 ? plannedInhabitantCount : 'None expected'}</div>
            </div>
            <div className="empty-grid">
              <strong>Population Plan Total</strong>
              <div>{residentTranchePlanTotal}</div>
            </div>
            {childPlacePlanSummary && (
              <div className="empty-grid explore-detail-span">
                <strong>Sub-Place Plan</strong>
                <div>{childPlacePlanSummary}</div>
              </div>
            )}
            <div className="empty-grid explore-detail-span">
              <strong>Description</strong>
              <div>{draftPlace.description || draftPlace.entityDesc || ''}</div>
            </div>
          </div>
        </section>
      )}

      {draftPlace && (
        <section className="editor-section">
          <div className="section-header town-suggestion-header">
            <h3>Sub-Place Suggestions</h3>
            <span className="existing-list-title">Sub-places: {subPlaceProgressText}</span>
            <span className="existing-list-title">Population: {populationProgressText}</span>
            <div className="button-row compact">
              <button
                type="button"
                className="secondary-button button-with-icon"
                disabled={isBusy || isDraftStructureLocked}
                onClick={() => runDraftSubPlaceSuggestionPass({ replace: true })}
              >
                {activeAction === 'suggest-subplaces' ? 'Suggesting...' : 'Refresh'}
              </button>
              <button
                type="button"
                className="secondary-button button-with-icon"
                disabled={isBusy || isSubPlacePlanComplete || isDraftStructureLocked}
                onClick={() => runDraftSubPlaceSuggestionPass()}
              >
                {activeAction === 'suggest-subplaces' ? 'Suggesting...' : 'More'}
              </button>
              <button
                type="button"
                className="secondary-button button-with-icon"
                disabled={isBusy || plannedSubPlaceCount <= 0 || isSubPlacePlanComplete || isDraftStructureLocked}
                onClick={() => runDraftSubPlaceSuggestionPass({ all: true })}
                title="Create the remaining sub-place suggestions one batch at a time"
              >
                {activeAction === 'suggest-all-subplaces' ? 'Creating All SubPlaces...' : 'Create All SubPlaces'}
              </button>
            </div>
          </div>
          <div className="town-population-panel">
            <div className="town-population-heading">
              <div>
                <strong>Permanent Population</strong>
                <span>{populationProgressText}</span>
              </div>
              <div className="button-row compact">
                {isHouseholdManifestTranche(selectedPopulationTranche) && (
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    disabled={
                      isBusy ||
                      (!canPlanSelectedHouseholds &&
                        !canRegenerateSelectedHouseholdManifest)
                    }
                    onClick={() => runDraftHouseholdManifestPlan({
                      populationTranche: selectedPopulationTranche,
                      replace: Boolean(selectedHouseholdManifest),
                    })}
                    title={
                      selectedHouseholdManifest
                        ? `Replace the ${selectedPopulationTrancheLabel.toLowerCase()} plan before generating its residents`
                        : `Plan every ${selectedPopulationTrancheLabel.toLowerCase()} household before generating residents`
                    }
                  >
                    {activeAction === 'plan-households'
                      ? 'Planning...'
                      : selectedHouseholdManifest
                        ? 'Regenerate Plan'
                        : `Plan ${selectedPopulationTrancheLabel}`}
                  </button>
                )}
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  disabled={isBusy || !canGenerateSelectedPopulationTranche}
                  onClick={() => runDraftInhabitantSuggestionPass({
                    populationTranche: selectedPopulationTranche,
                  })}
                  title={
                    canGenerateSelectedPopulationTranche
                      ? `Generate the next ${selectedPopulationTrancheLabel.toLowerCase()} batch in memory`
                      : selectedTrancheNeedsHouseholdManifest
                        ? `Plan ${selectedPopulationTrancheLabel.toLowerCase()} first`
                        : 'Complete the sub-place plan and preceding population phases first'
                  }
                >
                  {activeAction === 'suggest-inhabitants'
                    ? 'Generating...'
                    : `Generate ${selectedPopulationTrancheLabel} Batch`}
                </button>
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  disabled={isBusy || !canGenerateSelectedPopulationTranche}
                  onClick={() => runDraftInhabitantSuggestionSeries({
                    populationTranche: selectedPopulationTranche,
                  })}
                  title={
                    canGenerateSelectedPopulationTranche
                      ? `Generate every remaining ${selectedPopulationTrancheLabel.toLowerCase()} batch serially`
                      : selectedTrancheNeedsHouseholdManifest
                        ? `Plan ${selectedPopulationTrancheLabel.toLowerCase()} first`
                        : 'Complete the sub-place plan and preceding population phases first'
                  }
                >
                  {activeAction === 'complete-inhabitant-tranche'
                    ? 'Completing...'
                    : `Complete ${selectedPopulationTrancheLabel}`}
                </button>
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  disabled={isBusy || !canGenerateAllRemainingPopulation}
                  onClick={() => runDraftInhabitantSuggestionSeries()}
                  title={
                    canGenerateAllRemainingPopulation
                      ? 'Generate every remaining permanent-resident batch serially'
                      : 'The planned permanent population is complete'
                  }
                >
                  {activeAction === 'suggest-all-inhabitants'
                    ? 'Generating All...'
                    : 'Generate All Remaining'}
                </button>
                {isSerialPopulationGeneration && (
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    disabled={isPopulationGenerationStopRequested}
                    onClick={stopPopulationGenerationAfterCurrentBatch}
                    title="Stop after the currently running population batch returns"
                  >
                    {isPopulationGenerationStopRequested
                      ? 'Stopping...'
                      : 'Stop After Current Batch'}
                  </button>
                )}
                {(populationTrancheCounts[selectedPopulationTranche] ?? 0) > 0 && (
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    disabled={isBusy || !canRefreshSelectedPopulationTranche}
                    onClick={() => runDraftInhabitantSuggestionPass({
                      replace: true,
                      populationTranche: selectedPopulationTranche,
                    })}
                    title={
                      hasGeneratedLaterTranche
                        ? 'A dependent later phase has already been generated'
                        : `Replace the current ${selectedPopulationTrancheLabel.toLowerCase()} drafts in memory`
                    }
                  >
                    {activeAction === 'suggest-inhabitants'
                      ? 'Generating...'
                      : `Refresh ${selectedPopulationTrancheLabel}`}
                  </button>
                )}
              </div>
            </div>
            {!isPopulationPlanValid && (
              <div className="panel-status error-status">
                The four population phases total {residentTranchePlanTotal}, but the planned
                population is {plannedPopulation}.
              </div>
            )}
            <div className="town-population-tranches" role="group" aria-label="Population phase">
              {POPULATION_TRANCHES.map(({ key, label }) => {
                const planEntry = residentTranchePlan.find(({ tranche }) => tranche === key);
                const count = populationTrancheCounts[key] ?? 0;
                const target = planEntry?.expectedCount ?? 0;
                const isComplete = isPopulationPlanValid && count >= target;

                return (
                  <button
                    key={key}
                    type="button"
                    className={`town-population-tranche population-tranche-${key}${selectedPopulationTranche === key ? ' active' : ''}`}
                    aria-pressed={selectedPopulationTranche === key}
                    onClick={() => setSelectedPopulationTranche(key)}
                    disabled={isBusy}
                  >
                    <span>
                      <strong>{label}</strong>
                      <small>{planEntry?.description || 'No phase description returned.'}</small>
                    </span>
                    <span className={isComplete ? 'complete' : ''}>{count} / {target}</span>
                  </button>
                );
              })}
            </div>
            {selectedHouseholdManifest && selectedHouseholdManifestProgress && (
              <div className="town-household-manifest">
                <div className="town-household-manifest-heading">
                  <strong>{selectedPopulationTrancheLabel} Plan</strong>
                  <span>
                    {selectedHouseholdManifestProgress.generatedCount} /{' '}
                    {selectedHouseholdManifestProgress.targetCount} generated
                  </span>
                </div>
                <div className="town-household-manifest-table">
                  <div className="town-household-manifest-row town-household-manifest-head">
                    <span>Household</span>
                    <span>Home</span>
                    <span>Anchors</span>
                    <span>Planned Members</span>
                    <span>Progress</span>
                  </div>
                  {selectedHouseholdManifestProgress.households.map((household) => {
                    const homeName = draftChildSuggestions.find(
                      (place) =>
                        String(place?.draftPlaceId ?? place?.idEntity ?? '').trim() ===
                        household.homePlaceId
                    )?.name || household.homePlaceId;

                    return (
                      <div
                        key={household.householdKey}
                        className="town-household-manifest-row"
                      >
                        <span>
                          <strong>{household.householdName}</strong>
                          <small>{household.householdConcept}</small>
                        </span>
                        <span>{homeName}</span>
                        <span>{household.anchorResidentNames.join(', ')}</span>
                        <span>
                          {household.memberSlots.length > 0 ? (
                            <details>
                              <summary>{household.memberSlots.length} planned</summary>
                              <ul>
                                {household.memberSlots.map((slot) => (
                                  <li key={slot.slotId}>
                                    <strong>{slot.name}</strong>
                                    {' / '}
                                    {slot.householdRole}
                                    {' / '}
                                    {slot.lifeStage}
                                    <small>{slot.relationshipDetail}</small>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          ) : (
                            'No additions'
                          )}
                        </span>
                        <span className={household.complete ? 'complete' : ''}>
                          {household.generatedCount} / {household.targetCount}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="town-population-note">
              Plan identities here, then prepare individual Being records after reviewing Events. Travelers, visitors, caravan crews, and other transient
              people are excluded.
            </div>
          </div>
          {notableTargetCount > 0 && (
            <div className="town-commercial-inventory-panel town-notable-loadout-panel">
              <div className="town-commercial-inventory-heading">
                <div>
                  <strong>Notable Loadouts</strong>
                  <span>
                    {notableLoadoutProgress
                      ? `${notableLoadoutProgress.generatedCount} / ${notableLoadoutProgress.targetCount} key Item drafts generated for ${notableDraftCount} notables`
                      : notableDraftCount === notableTargetCount
                        ? `${notableDraftCount} completed notables awaiting loadout review`
                        : `${notableDraftCount} / ${notableTargetCount} notables generated`}
                  </span>
                </div>
                <div className="button-row compact">
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    disabled={
                      isBusy ||
                      (!canPlanNotableLoadouts &&
                        !canRegenerateNotableLoadoutPlan)
                    }
                    onClick={() =>
                      runDraftNotableLoadoutPlan({
                        replace: Boolean(draftNotableLoadoutManifest),
                      })
                    }
                    title={
                      draftNotableLoadoutManifest
                        ? 'Replace the reviewed loadout plan before Item generation starts'
                        : 'Plan key possessions and personal currency for every notable'
                    }
                  >
                    {activeAction === 'plan-notable-loadouts'
                      ? 'Planning...'
                      : draftNotableLoadoutManifest
                        ? 'Regenerate Plan'
                        : 'Plan Notable Loadouts'}
                  </button>
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    disabled={isBusy || !canGenerateNotableLoadouts}
                    onClick={() =>
                      runDraftNotableLoadoutSeries({ singleBatch: true })
                    }
                    title={`Generate only the next ${NOTABLE_LOADOUT_BATCH_SIZE} or fewer complete notable Item drafts`}
                  >
                    {activeAction === 'generate-notable-loadout-batch'
                      ? 'Generating Batch...'
                      : 'Generate Next Batch'}
                  </button>
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    disabled={isBusy || !canGenerateNotableLoadouts}
                    onClick={() => runDraftNotableLoadoutSeries()}
                    title="Generate every remaining notable Item in serial batches"
                  >
                    {activeAction === 'generate-notable-loadouts'
                      ? 'Generating...'
                      : 'Generate All Remaining'}
                  </button>
                  {isSerialNotableLoadoutGeneration && (
                    <button
                      type="button"
                      className="secondary-button button-with-icon"
                      disabled={isNotableLoadoutStopRequested}
                      onClick={stopNotableLoadoutAfterCurrentBatch}
                      title="Stop after the currently running notable Item batch returns"
                    >
                      {isNotableLoadoutStopRequested
                        ? 'Stopping...'
                        : 'Stop After Current Batch'}
                    </button>
                  )}
                </div>
              </div>
              {notableLoadoutProgress ? (
                <div className="town-commercial-inventory-table">
                  <div className="town-commercial-inventory-row town-commercial-inventory-head town-notable-loadout-row">
                    <span>Notable</span>
                    <span>Loadout Plan</span>
                    <span>Generated Items</span>
                    <span>Currency</span>
                    <span>Progress</span>
                  </div>
                  {notableLoadoutProgress.notables.map((notable) => {
                    const generatedItems = draftNotableLoadoutItems.filter(
                      (item) =>
                        String(item?.ownerDraftBeingId ?? '').trim() ===
                        notable.draftBeingId
                    );
                    const currencyHolding = draftNotableCurrencyHoldings.find(
                      (holding) =>
                        String(holding?.draftBeingId ?? '').trim() ===
                        notable.draftBeingId
                    );

                    return (
                      <div
                        key={notable.draftBeingId}
                        className="town-commercial-inventory-row town-notable-loadout-row"
                      >
                        <span>
                          <strong>{notable.notableName}</strong>
                          <small>{notable.loadoutConcept}</small>
                          <small>{notable.personalWealthProfile}</small>
                        </span>
                        <span>
                          <details>
                            <summary>
                              {notable.itemSlots.length} planned Item
                              {notable.itemSlots.length === 1 ? '' : 's'}
                            </summary>
                            <ul>
                              {notable.itemSlots.map((slot) => (
                                <li key={slot.notableItemSlotId}>
                                  <strong>{slot.itemPurpose}</strong>
                                  {' / '}
                                  {slot.powerTier}
                                  <small>{slot.itemConcept}</small>
                                </li>
                              ))}
                            </ul>
                          </details>
                        </span>
                        <span>
                          {generatedItems.length > 0 ? (
                            <details>
                              <summary>{generatedItems.length} generated</summary>
                              <ul>
                                {generatedItems.map((item) => (
                                  <li key={item.notableItemSlotId}>
                                    <strong>{item.name}</strong>
                                    {' / '}
                                    {item.itemType}
                                    {' / '}
                                    {formatGoldValue(item.value)}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          ) : (
                            'Not generated'
                          )}
                        </span>
                        <span>
                          <strong>
                            {formatGoldValue(currencyHolding?.actualCashGp)}
                          </strong>
                          <small>
                            Target {formatGoldValue(
                              currencyHolding?.targetCashGp ??
                                notable.personalCashTargetGp
                            )}
                          </small>
                          <small>
                            {formatCurrencyBreakdown(currencyHolding?.currencies)}
                          </small>
                        </span>
                        <span className={notable.complete ? 'complete' : ''}>
                          {notable.generatedCount} / {notable.targetCount}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="town-population-note">
                  Complete the notable population, then review one AI-authored
                  loadout and personal currency plan before generating full Item
                  drafts.
                </div>
              )}
              <div className="town-population-note">
                Notable loadouts, Items, and currency are drafts until Save Place Tree persists them.
                {draftNotableLoadoutManifest?.templates?.length > 0 && <span> {draftNotableLoadoutManifest.templates.length} shared item templates supply {notableLoadoutProgress?.targetCount || 0} independent assigned possessions.</span>}
              </div>
            </div>
          )}
          {inventoryDraftPlaces.length > 0 && (
            <div className="town-commercial-inventory-panel">
              <div className="town-commercial-inventory-heading">
                <div>
                  <strong>Trade Inventory</strong>
                  <span>
                    {commercialInventoryProgress
                      ? `${commercialInventoryProgress.generatedCount} / ${commercialInventoryProgress.targetCount} Item drafts generated across ${commercialInventoryProgress.stockedPlaceCount} stocked place${commercialInventoryProgress.stockedPlaceCount === 1 ? '' : 's'}`
                      : `${inventoryDraftPlaces.length} Commercial, Industrial, and Agricultural place${inventoryDraftPlaces.length === 1 ? '' : 's'} awaiting inventory review`}
                  </span>
                </div>
                <div className="button-row compact">
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    disabled={
                      isBusy ||
                      (!canPlanCommercialInventory &&
                        !canRegenerateCommercialInventoryPlan)
                    }
                    onClick={() =>
                      runDraftCommercialInventoryPlan({
                        replace: Boolean(draftCommercialInventoryManifest),
                      })
                    }
                    title={
                      draftCommercialInventoryManifest
                        ? 'Replace the stock plan before Item generation starts'
                        : 'Evaluate trade inventory for Commercial, Industrial, and Agricultural places'
                    }
                  >
                    {activeAction === 'plan-commercial-inventory'
                      ? 'Planning...'
                      : draftCommercialInventoryManifest
                        ? 'Regenerate Inventory Plan'
                        : 'Plan Trade Inventories'}
                  </button>
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    disabled={isBusy || !canGenerateCommercialInventory}
                    onClick={() =>
                      runDraftCommercialInventorySeries({ singleBatch: true })
                    }
                    title={`Generate only the next ${COMMERCIAL_INVENTORY_BATCH_SIZE} or fewer complete Item drafts for review`}
                  >
                    {activeAction === 'generate-commercial-inventory-batch'
                      ? 'Generating Batch...'
                      : 'Generate Next Batch'}
                  </button>
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    disabled={isBusy || !canGenerateCommercialInventory}
                    onClick={() => runDraftCommercialInventorySeries()}
                    title="Generate every remaining planned trade Item in serial batches"
                  >
                    {activeAction === 'generate-commercial-inventory'
                      ? 'Generating...'
                      : 'Generate All Remaining'}
                  </button>
                  {isSerialInventoryGeneration && (
                    <button
                      type="button"
                      className="secondary-button button-with-icon"
                      disabled={isInventoryGenerationStopRequested}
                      onClick={stopInventoryGenerationAfterCurrentBatch}
                      title="Stop after the currently running inventory batch returns"
                    >
                      {isInventoryGenerationStopRequested
                        ? 'Stopping...'
                        : 'Stop After Current Batch'}
                    </button>
                  )}
                </div>
              </div>
              {draftCommercialInventoryManifest && commercialInventoryProgress ? (
                <div className="town-commercial-inventory-table">
                  <div className="town-commercial-inventory-row town-commercial-inventory-head town-trade-inventory-row">
                    <span>Place</span>
                    <span>Stock Plan</span>
                    <span>Generated Items</span>
                    <span>Cash Reserve</span>
                    <span>Progress</span>
                  </div>
                  {commercialInventoryProgress.placeInventories.map(
                    (placeInventory) => {
                      const generatedItems = draftCommercialInventoryItems.filter(
                        (item) =>
                          String(item?.commercialPlaceId ?? '').trim() ===
                          String(placeInventory?.commercialPlaceId ?? '').trim()
                      );
                      const plannedTypeCount = Array.isArray(
                        placeInventory?.inventoryTypes
                      )
                        ? placeInventory.inventoryTypes.length
                        : 0;
                      const currencyHolding = draftEstablishmentCurrencyHoldings.find(
                        (holding) =>
                          String(holding?.commercialPlaceId ?? '').trim() ===
                          String(placeInventory?.commercialPlaceId ?? '').trim()
                      );

                      return (
                        <div
                          key={placeInventory.commercialPlaceId}
                          className="town-commercial-inventory-row town-trade-inventory-row"
                        >
                          <span>
                            <strong>{placeInventory.commercialPlaceName}</strong>
                            <small>{placeInventory.shopConcept}</small>
                            <small>{placeInventory.inventoryDecision}</small>
                          </span>
                          <span>
                            {placeInventory.stocksTradeableItems ? (
                              <details>
                                <summary>
                                  {plannedTypeCount} stock type{plannedTypeCount === 1 ? '' : 's'}
                                </summary>
                                <ul>
                                  {placeInventory.inventoryTypes.map(
                                    (inventoryType) => (
                                      <li key={inventoryType.inventoryTypeKey}>
                                        <strong>{inventoryType.inventoryType}</strong>
                                        {' / '}
                                        {inventoryType.itemCount} Item
                                        {inventoryType.itemCount === 1 ? '' : 's'}
                                        <small>{inventoryType.stockGuidance}</small>
                                      </li>
                                    )
                                  )}
                                </ul>
                              </details>
                            ) : (
                              'No trade stock planned'
                            )}
                          </span>
                          <span>
                            {generatedItems.length > 0 ? (
                              <details>
                                <summary>
                                  {generatedItems.length} generated
                                </summary>
                                <ul>
                                  {generatedItems.map((item) => (
                                    <li key={item.inventorySlotId}>
                                      <strong>{item.name}</strong>
                                      {' / '}
                                      {item.itemType}
                                      {' / Qty '}
                                      {item.quantity}
                                      {' / '}
                                      {item.value} gold
                                    </li>
                                  ))}
                                </ul>
                              </details>
                            ) : placeInventory.stocksTradeableItems ? (
                              'Not generated'
                            ) : (
                              'Not stocked'
                            )}
                          </span>
                          <span>
                            {placeInventory.stocksTradeableItems ? (
                              currencyHolding ? (
                                <>
                                  <strong>
                                    {formatGoldValue(currencyHolding.actualCashGp)}
                                  </strong>
                                  <small>
                                    Stock {formatGoldValue(
                                      currencyHolding.inventoryRetailValueGp
                                    )}
                                  </small>
                                  <small>
                                    {(Number(currencyHolding.cashReserveRatio) * 100)
                                      .toLocaleString(undefined, {
                                        maximumFractionDigits: 1,
                                      })}% reserve / target{' '}
                                    {formatGoldValue(currencyHolding.targetCashGp)}
                                  </small>
                                  <small>
                                    {formatCurrencyBreakdown(
                                      currencyHolding.currencies
                                    )}
                                  </small>
                                </>
                              ) : (
                                <>
                                  <strong>
                                    {(Number(placeInventory.cashReserveRatio) * 100)
                                      .toLocaleString(undefined, {
                                        maximumFractionDigits: 1,
                                      })}% planned
                                  </strong>
                                  <small>
                                    {placeInventory.cashReserveProfile}
                                  </small>
                                  <small>Calculated when stock is complete</small>
                                </>
                              )
                            ) : (
                              'No cashbox planned'
                            )}
                          </span>
                          <span className={placeInventory.complete ? 'complete' : ''}>
                            {placeInventory.stocksTradeableItems
                              ? `${placeInventory.generatedCount} / ${placeInventory.targetCount}`
                              : 'Not stocked'}
                          </span>
                        </div>
                      );
                    }
                  )}
                </div>
              ) : (
                <div className="town-population-note">
                  The first AI call decides which eligible places trade goods and plans
                  their stock types and exact counts. Item drafts are generated only
                  after that plan is reviewed.
                </div>
              )}
              <div className="town-population-note">
                Inventory plans remain drafts. Save Place Tree persists generated Items and stock quantities.
              </div>
            </div>
          )}
          <div className="editor-table">
            <div className="editor-table-row editor-table-head town-subplace-suggestions-grid">
              <span>Name</span>
              <span>Parent</span>
              <span>Place Type</span>
              <span>Zone Type</span>
              <span>Building Type</span>
              <span>Description</span>
            </div>
            {draftChildSuggestions.map((suggestion, index) => {
              const linkedInhabitants = getLinkedInhabitantSuggestions(
                draftInhabitantSuggestions,
                suggestion.draftPlaceId
              );
              const linkedInventoryItems = draftCommercialInventoryItems.filter(
                (item) =>
                  String(item?.commercialPlaceId ?? '').trim() ===
                  String(suggestion?.draftPlaceId ?? suggestion?.idEntity ?? '').trim()
              );

              return (
                <details
                  key={suggestion.draftPlaceId}
                  className="town-subplace-suggestion-group"
                >
                  <summary className="town-subplace-suggestion-summary">
                    <div className="editor-table-row town-subplace-suggestions-grid">
                      <span>
                        {suggestion.name}
                        {(linkedInhabitants.length > 0 ||
                          linkedInventoryItems.length > 0) && (
                          <small className="town-subplace-related-counts">
                            {linkedInhabitants.length} Being
                            {linkedInhabitants.length === 1 ? '' : 's'}
                            {' / '}
                            {linkedInventoryItems.length} Item
                            {linkedInventoryItems.length === 1 ? '' : 's'}
                          </small>
                        )}
                        {suggestion.savedPlaceName && (
                          <small className="saved-suggestion-label">Saved as {suggestion.savedPlaceName}</small>
                        )}
                      </span>
                      <span>
                        {suggestion.parentSuggestionName || getPlaceLabel(draftPlace)}
                      </span>
                      <span>{suggestion.placeType}</span>
                      <span>{suggestion.zoneType}</span>
                      <span>{suggestion.buildingType}</span>
                      <span>{suggestion.description}</span>
                    </div>
                  </summary>
                  {linkedInhabitants.length > 0 && (
                    <div className="town-subplace-inhabitants">
                      <div className="town-subplace-inhabitants-heading">
                        <strong>Linked Inhabitants</strong>
                        <span>{linkedInhabitants.length}</span>
                      </div>
                      {linkedInhabitants.map(({
                        suggestion: inhabitant,
                        index: inhabitantIndex,
                        relationships,
                      }) => {
                        const notableItems = draftNotableLoadoutItems.filter(
                          (item) =>
                            String(item?.ownerDraftBeingId ?? '').trim() ===
                            String(inhabitant?.draftBeingId ?? '').trim()
                        );

                        return (
                          <div
                            key={`${suggestion.draftPlaceId}-${inhabitant.draftBeingId || inhabitantIndex}`}
                            className={`town-inhabitant-suggestion-row population-tranche-${inhabitant.populationTranche}`}
                          >
                            <span>
                              <strong>{inhabitant.name}</strong>
                              {inhabitant.savedBeingName && (
                                <small className="saved-suggestion-label">Saved as {inhabitant.savedBeingName}</small>
                              )}
                            </span>
                            <span>
                              {inhabitant.title}
                              <small className="saved-suggestion-label">
                                {[inhabitant.beingType, `Level ${inhabitant.level}`].filter(Boolean).join(' / ')}
                              </small>
                              <small className="saved-suggestion-label">
                                {[
                                  POPULATION_TRANCHES.find(
                                    ({ key }) => key === inhabitant.populationTranche
                                  )?.label,
                                  inhabitant.householdName,
                                  inhabitant.householdRole,
                                ].filter(Boolean).join(' / ')}
                              </small>
                              {Array.isArray(inhabitant.currencies) &&
                                inhabitant.currencies.length > 0 && (
                                  <small className="saved-suggestion-label">
                                    Cash {formatGoldValue(inhabitant.currencyActualGp)}
                                    {' / '}
                                    {formatCurrencyBreakdown(inhabitant.currencies)}
                                  </small>
                                )}
                              {notableItems.length > 0 && (
                                <details>
                                  <summary>
                                    {notableItems.length} notable Item
                                    {notableItems.length === 1 ? '' : 's'}
                                  </summary>
                                  <ul>
                                    {notableItems.map((item) => (
                                      <li key={item.notableItemSlotId}>
                                        {item.name} / {item.itemType}
                                      </li>
                                    ))}
                                  </ul>
                                </details>
                              )}
                            </span>
                            <span className="town-inhabitant-relationship-list">
                              {(relationships.length > 0
                                ? relationships
                                : [{ relateType: 'Resident', reason: 'Direct inhabitant of this place.' }]
                              ).map((relationship, relationshipIndex) => (
                                <span
                                  key={`${relationship.relateType}-${relationshipIndex}`}
                                  className="town-inhabitant-relationship"
                                  title={relationship.reason || relationship.relateType}
                                >
                                  {relationship.relateType}
                                </span>
                              ))}
                            </span>
                            <span>{inhabitant.description}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {linkedInventoryItems.length > 0 && (
                    <div className="town-subplace-items">
                      <div className="town-subplace-inhabitants-heading">
                        <strong>Linked Items</strong>
                        <span>{linkedInventoryItems.length}</span>
                      </div>
                      {linkedInventoryItems.map((item) => (
                        <div
                          key={item.inventorySlotId}
                          className="town-subplace-item-row"
                        >
                          <span>
                            <strong>{item.name}</strong>
                            <small>{item.title}</small>
                          </span>
                          <span>
                            {item.itemType}
                            <small>
                              {item.inventoryTypeKey}
                            </small>
                          </span>
                          <span>
                            Quantity {item.quantity}
                            <small>{item.value} gold each</small>
                          </span>
                          <span>{item.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </details>
              );
            })}
            {draftChildSuggestions.length === 0 && (
              <div className="empty-grid">No sub-place suggestions.</div>
            )}
          </div>
        </section>
      )}

      {draftPlace && <PlaceTreeDevelopment
        draft={worldDraft} development={worldDevelopment} isBusy={isBusy}
        onAction={setActiveAction} onStatus={setStatusMessage} onError={setErrorMessage}
        onChange={next => {
          setWorldDevelopment(next);
          latestSessionDraftRef.current = { ...latestSessionDraftRef.current, worldDevelopment: next };
          const backup = savePlaceTreeSessionDraft(latestSessionDraftRef.current);
          monitorPlaceTreeBackup(backup, setErrorMessage, persistenceErrorRef);
          if (!backup.ok) addLogEntry('World development progress could not be backed up in this tab. Keep the tab open to resume.');
        }}
        onPrepared={(entry, record) => {
          if (entry.kind === 'Being' && !entry.entity.isNearby && entry.entity.populationTranche !== 'nearby') {
            setDraftInhabitantSuggestions(current => current.map(being => being.draftBeingId === entry.draftId
              ? { ...being, ...record, currencies: being.currencies, identityOnly: false } : being));
          }
        }}
      />}

      {draftPlace && (
        <section className="editor-section">
          <h3>Events</h3>
          <p>Generate incidents and history using the current Places, Beings, and Items. Review these drafts before saving the Place Tree.</p>
          <div className="form-actions">
            <label className="editor-field">
              Events per batch
              <input type="number" min="1" max="20" value={eventCount}
                disabled={isBusy}
                onChange={event => setEventCount(Math.max(1, Math.min(20, Number(event.target.value) || 1)))} />
            </label>
            <button type="button" className="button-with-icon" onClick={generateEvents}
              disabled={isBusy}>
              {activeAction === 'generate-events' ? 'Generating Events...' : 'Generate Events'}
            </button>
          </div>
          {draftEvents.map((event, index) => (
            <details key={event.draftEventId || index} className="editor-section">
              <summary>{event.summary} — {event.isPublic ? 'Public' : 'Private'}</summary>
              <p>{event.eventType} · {event.eventTimeAge}, year {event.eventTimeYear}, {event.eventTimeSeason}, day {event.eventTimeDay}</p>
              <label className="editor-field">Event summary
                <input className="text-input" value={event.summary} disabled={isBusy || Boolean(worldSaveProgress.savedIds[event.draftEventId])}
                  onChange={change => setDraftEvents(current => current.map((entry, i) =>
                    i === index ? { ...entry, summary: change.target.value } : entry))} />
              </label>
              <label className="editor-field">Event details
                <textarea className="text-input editor-textarea" value={event.details} disabled={isBusy || Boolean(worldSaveProgress.savedIds[event.draftEventId])}
                  onChange={change => setDraftEvents(current => current.map((entry, i) =>
                    i === index ? { ...entry, details: change.target.value } : entry))} />
              </label>
              <label><input type="checkbox" checked={event.isPublic} disabled={isBusy || Boolean(worldSaveProgress.savedIds[event.draftEventId])}
                onChange={change => setDraftEvents(current => current.map((entry, i) =>
                  i === index ? { ...entry, isPublic: change.target.checked } : entry))} /> Public knowledge</label>
              <ul>{(event.participants || []).map((participant, i) => {
                const entity = getWorldDraftCatalog(worldDraft).find(entry => entry.draftId === participant.entityDraftId)?.entity;
                return <li key={i}>{entity?.name || participant.entityDraftId}: {participant.role} ({participant.relateType}; {participant.knows ? 'knows' : 'unaware'})</li>;
              })}</ul>
              <p>Known by: {(event.knownByEntityIds || []).map(id =>
                getWorldDraftCatalog(worldDraft).find(entry => entry.draftId === id)?.entity?.name || id).join(', ') || 'Only aware participants'}</p>
              <button type="button" disabled={isBusy || Boolean(worldSaveProgress.savedIds[event.draftEventId]) ||
                (worldDevelopment.relationships || []).some(relation => relation.eventDraftIds?.includes(event.draftEventId))}
                title="Remove relationships citing this event before removing the event."
                onClick={() => setDraftEvents(current => current.filter((_, i) => i !== index))}>Remove event</button>
            </details>
          ))}
          {!draftEvents.length && <div className="empty-grid">No event drafts yet.</div>}
        </section>
      )}

      {(draftPlace || graph.rootPlaceId || lastRunSummary) && (
        <section className="editor-section">
          <h3>Summary</h3>
          <div className="explore-summary-grid">
            <div className="empty-grid"><strong>Events</strong><div>{draftEvents.length}</div></div>
            <div className="empty-grid">
              <strong>Places</strong>
              <div>{draftSummaryCounts.places}</div>
            </div>
            <div className="empty-grid">
              <strong>Residents</strong>
              <div>{draftSummaryCounts.residents}</div>
            </div>
            <div className="empty-grid">
              <strong>Notable Items</strong>
              <div>{draftSummaryCounts.notableItems}</div>
            </div>
            <div className="empty-grid">
              <strong>Trade Items</strong>
              <div>{draftSummaryCounts.tradeItems}</div>
            </div>
            <div className="empty-grid">
              <strong>Currency Holdings</strong>
              <div>{draftSummaryCounts.currencyHolders}</div>
            </div>
            <div className="empty-grid">
              <strong>Last Run</strong>
              <div>{lastRunSummary?.action || 'None'}</div>
            </div>
            <div className="empty-grid">
              <strong>Failures</strong>
              <div>{lastRunSummary?.failures ?? 0}</div>
            </div>
          </div>
          {lastRunSummary && (
            <div className="empty-grid explore-last-run-grid">
              <strong>Database Records Created This Run</strong>
              <span>
                {lastRunSummary.placesCreated} Place record
                {lastRunSummary.placesCreated === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </section>
      )}

      {rootNode && (
        <section className="editor-section">
          <h3>Place Tree</h3>
          <div className="place-tree explore-tree">
            <GenerateTownTreeNode
              node={rootNode}
              depth={0}
              selectedNodeKey={selectedNodeKey}
              onSelect={setSelectedNodeKey}
            />
          </div>
        </section>
      )}

      </>}

      {logEntries.length > 0 && (
        <section className="editor-section">
          <h3>Progress</h3>
          <ol className="explore-log">
            {logEntries.map((entry, index) => (
              <li key={`${index}-${entry}`}>{entry}</li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

export default GenerateTown;
